import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogoMark, API } from "@/assets";
import {
  Loader2, LogOut, RefreshCw, Inbox, Clock, Check, CheckCheck, X,
  UploadCloud, Trash2, Music2, CalendarCheck, Users, Newspaper,
  ShoppingBag, Quote, PenLine, Pencil, Package, Truck, Sparkles, Copy, ImagePlus,
  Mic2, Briefcase, FileAudio, FileText, Send, Megaphone, UserCog, ShieldCheck,
  Activity, Crown, ClipboardList, Star, CalendarClock, FileUp, UserPlus, Trophy, Gamepad2, LifeBuoy, BookOpen,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { streamSSE } from "@/components/sse";
import { LabelOpsGuideContent } from "@/components/LabelOpsGuideContent";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full bg-[#14141A]/80 border border-[#282836] rounded-xl px-4 py-3 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-cyan-400/80 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-300";

const STATUS_STYLE = {
  new: "bg-venom/15 text-venom border-venom/50",
  contacted: "bg-crystal/15 text-[#B983FF] border-crystal/50",
  closed: "bg-white/8 text-ice/60 border-white/20",
  pending_payment: "bg-crystal/15 text-[#B983FF] border-crystal/50",
  confirmed: "bg-venom/15 text-venom border-venom/50",
  reviewing: "bg-crystal/15 text-[#B983FF] border-crystal/50",
  callback: "bg-venom/15 text-venom border-venom/50",
  interview: "bg-crystal/15 text-[#B983FF] border-crystal/50",
  signed: "bg-venom/25 text-venom border-venom/70",
  hired: "bg-venom/25 text-venom border-venom/70",
};

async function api(path, opts = {}, retry = true) {
  const res = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  if (res.status === 401 && retry) {
    const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
    if (r.ok) return api(path, opts, false);
  }
  return res;
}

const jsonApi = (path, opts = {}) =>
  api(path, { headers: { "Content-Type": "application/json" }, ...opts });

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const mediaUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND}${url}`);

const TABS = [
  { id: "leads", label: "Leads", Icon: Inbox, roles: ["executive", "manager"] },
  { id: "talent", label: "Talent", Icon: Mic2, roles: ["executive", "manager", "aar"] },
  { id: "careers", label: "Careers", Icon: Briefcase, roles: ["executive", "manager"] },
  { id: "bookings", label: "Bookings", Icon: CalendarCheck, roles: ["executive", "manager", "engineer", "artist"] },
  { id: "tracks", label: "Tracks", Icon: Music2, roles: ["executive", "manager", "engineer", "artist"] },
  { id: "press", label: "Press", Icon: Newspaper, roles: ["executive", "manager"] },
  { id: "pitches", label: "Pitches", Icon: Megaphone, roles: ["executive", "manager"] },
  { id: "merch", label: "Merch", Icon: ShoppingBag, roles: ["executive", "manager"] },
  { id: "orders", label: "Orders", Icon: Package, roles: ["executive", "manager"] },
  { id: "reviews", label: "Reviews", Icon: Quote, roles: ["executive", "manager"] },
  { id: "writer", label: "AI Writer", Icon: Sparkles, roles: ["executive", "manager"] },
  { id: "subscribers", label: "Subscribers", Icon: Users, roles: ["executive", "manager"] },
  { id: "team", label: "Team", Icon: UserCog, roles: ["executive"] },
  { id: "hr", label: "HR Suite", Icon: UserPlus, roles: ["executive", "manager"] },
  { id: "medusa", label: "Medusa", Icon: Crown, roles: ["executive", "manager"] },
  { id: "arcade", label: "Arcade", Icon: Gamepad2, roles: ["executive", "manager"] },
  { id: "drops", label: "Drop Planner", Icon: CalendarClock, roles: ["executive", "manager"] },
  { id: "demos", label: "Demo Queue", Icon: FileAudio, roles: ["executive", "manager", "aar"] },
  { id: "activity", label: "Activity", Icon: Activity, roles: ["executive", "manager"] },
  { id: "content", label: "Site Text", Icon: PenLine, roles: ["executive"] },
  { id: "knowledge", label: "Knowledge", Icon: BookOpen, roles: ["executive", "manager"] },
  { id: "guide", label: "Guide", Icon: LifeBuoy, roles: ["executive", "manager", "artist", "engineer", "aar"] },
];

// Label-Ops role identity — neon badge styling per role (purple = command, teal = ops, green = roster, amber = studio, fuchsia = scouting)
const ROLE_META = {
  executive: { label: "Admin / Executive", badge: "bg-purple-950/80 text-purple-300 border-purple-500/50 shadow-[0_0_12px_rgba(168,85,247,0.35)]", dot: "bg-purple-400", desc: "Full command — every tab, staff provisioning, site text. MFA required." },
  manager: { label: "Manager", badge: "bg-cyan-950/80 text-cyan-300 border-cyan-500/50 shadow-[0_0_12px_rgba(6,182,212,0.35)]", dot: "bg-cyan-400", desc: "Day-to-day label ops — leads, bookings, talent, merch, orders, press, subscribers." },
  artist: { label: "Artist", badge: "bg-emerald-950/80 text-emerald-300 border-emerald-500/50 shadow-[0_0_12px_rgba(57,255,20,0.35)]", dot: "bg-emerald-400", desc: "Roster artist — view studio sessions, upload tracks & demos to the catalog." },
  engineer: { label: "Engineer / Producer", badge: "bg-amber-950/80 text-amber-300 border-amber-500/50 shadow-[0_0_12px_rgba(245,158,11,0.35)]", dot: "bg-amber-400", desc: "Studio floor — session bookings + beat catalog management." },
  aar: { label: "A&R / Talent Scout", badge: "bg-fuchsia-950/80 text-fuchsia-300 border-fuchsia-500/50 shadow-[0_0_12px_rgba(217,70,239,0.35)]", dot: "bg-fuchsia-400", desc: "Talent pipeline — demos, follow-ups, roster approvals." },
  system: { label: "System", badge: "bg-white/8 text-ice/50 border-white/20", dot: "bg-ice/50", desc: "Automated platform event." },
};
const ROLE_LABELS = Object.fromEntries(Object.entries(ROLE_META).map(([k, v]) => [k, v.label]));

const RefreshBtn = ({ onClick, testId }) => (
  <button onClick={onClick} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] border border-white/15 rounded-full px-4 py-2 text-ice/70 hover:border-cyan-400/60 hover:text-cyan-300 transition-all" data-testid={testId}>
    <RefreshCw size={13} /> Refresh
  </button>
);

const DeleteBtn = ({ onClick, label, testId }) => (
  <button onClick={onClick} className="p-2 text-ice/40 hover:text-red-400 transition-colors" aria-label={label} data-testid={testId}>
    <Trash2 size={16} />
  </button>
);

const EditBtn = ({ onClick, label, testId }) => (
  <button onClick={onClick} className="p-2 text-ice/40 hover:text-cyan-300 transition-colors" aria-label={label} data-testid={testId}>
    <Pencil size={16} />
  </button>
);

// Small delivery badges — shows whether notification emails actually went out
const EmailBadges = ({ status, labels = ["owner", "client"] }) => {
  if (!status) return null;
  return (
    <span className="inline-flex flex-wrap gap-1.5" data-testid="email-status-badges">
      {labels.map((k) => status[k] ? (
        <span key={k} className={`font-mono text-[9px] uppercase tracking-[0.1em] rounded-full px-2 py-0.5 border ${status[k] === "sent" ? "text-emerald-300 border-emerald-500/40" : "text-red-300 border-red-500/50"}`} data-testid={`email-badge-${k}`}>
          {k === "owner" ? "Owner alert" : k === "client" ? "Client email" : "Applicant email"} {status[k] === "sent" ? "✓" : "✗ failed"}
        </span>
      ) : null)}
    </span>
  );
};

// Two-way messaging: staff writes a custom note, it emails the person and logs to a thread
function ReplyBox({ path, testId, thread }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setNote("");
    try {
      const res = await jsonApi(path, { method: "POST", body: JSON.stringify({ message: text.trim() }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Send failed.");
      setNote("Message sent and logged.");
      setText("");
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full mt-3" data-testid={`${testId}-replybox`}>
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-[#A855F7]/50 text-purple-300 rounded-full px-3.5 py-1.5 hover:bg-[#A855F7] hover:text-white transition-all" data-testid={`${testId}-reply-toggle`}>
        <Send size={12} /> {open ? "Close Reply" : `Reply by Email${thread?.length ? ` (${thread.length})` : ""}`}
      </button>
      {open && (
        <div className="mt-3 bg-[#0E0E12]/80 border border-purple-500/25 rounded-xl p-4">
          {(thread || []).length > 0 && (
            <div className="mb-3 space-y-2 max-h-40 overflow-y-auto" data-testid={`${testId}-thread`}>
              {thread.map((m, i) => (
                <div key={i} className="text-xs border-l-2 border-purple-500/40 pl-3 py-1">
                  <p className="text-ice/70 whitespace-pre-wrap">{m.body}</p>
                  <p className="text-[10px] font-mono text-ice/35 mt-0.5">{fmtDate(m.at)} · {m.sent ? "sent" : `FAILED${m.error ? ` — ${m.error}` : ""}`}</p>
                </div>
              ))}
            </div>
          )}
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your reply — it goes straight to their inbox, branded from the label…" className={`${inputCls} resize-none`} data-testid={`${testId}-reply-input`} />
          <div className="mt-2.5 flex items-center gap-3">
            <button onClick={send} disabled={busy || !text.trim()} className="inline-flex items-center gap-1.5 bg-[#A855F7] text-white font-bold text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all" data-testid={`${testId}-reply-send`}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
            </button>
            {note && <p className={`text-xs ${note.includes("sent") ? "text-emerald-300" : "text-red-300"}`} data-testid={`${testId}-reply-note`}>{note}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async (status = filter) => {
    const q = status !== "all" ? `?status=${status}` : "";
    const res = await jsonApi(`/quotes${q}`);
    if (res.ok) setLeads((await res.json()).leads || []);
  }, [filter]);

  useEffect(() => { load(filter); }, [filter, load]);

  const setStatus = async (quoteId, status) => {
    const res = await jsonApi(`/quotes/${quoteId}`, { method: "PATCH", body: JSON.stringify({ status }) });
    if (res.ok) {
      setLeads((ls) => ls.map((l) => (l.quote_id === quoteId ? { ...l, status } : l)));
      if (filter !== "all") load(filter); // keep filtered views honest — closed items leave the "new" list
    }
  };

  const counts = (leads || []).reduce((acc, l) => ({ ...acc, [l.status]: (acc[l.status] || 0) + 1 }), {});

  return (
    <div data-testid="leads-tab">
      <div className="grid grid-cols-3 gap-3 sm:gap-5" data-testid="leads-stats">
        {[
          { label: "New", value: counts.new || 0, icon: Inbox },
          { label: "Contacted", value: counts.contacted || 0, icon: Clock },
          { label: "Closed", value: counts.closed || 0, icon: CheckCheck },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-charcoal border border-crystal/20 rounded-xl p-4 sm:p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
              <Icon size={18} className="text-venom mb-2" />
              <p className="font-display text-2xl sm:text-3xl font-bold text-white">{s.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ice/50 mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="leads-filters">
        {[["all", "Active"], ["new", "New"], ["contacted", "Contacted"], ["closed", "Archived"]].map(([f, label]) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`font-mono text-[11px] uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-all duration-300 ${
              filter === f ? "bg-[#A855F7] text-white border-[#A855F7] font-semibold shadow-[0_0_16px_rgba(168,85,247,0.4)]" : "border-white/15 text-ice/60 hover:border-crystal/60 hover:text-ice"
            }`}
            data-testid={`filter-${f}`}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto"><RefreshBtn onClick={() => load()} testId="leads-refresh-button" /></span>
      </div>

      <div className="mt-6 space-y-3" data-testid="leads-list">
        {leads === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-cyan-400" size={26} /></div>
        ) : (filter === "all" ? leads.filter((l) => l.status !== "closed") : leads).length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/12 rounded-2xl" data-testid="leads-empty">
            <Inbox size={30} className="mx-auto text-ice/25 mb-3" />
            <p className="text-sm text-ice/45">{filter === "closed" ? "No archived tickets." : "No active quote requests — closed tickets move to the Archive."}</p>
          </div>
        ) : (
          (filter === "all" ? leads.filter((l) => l.status !== "closed") : leads).map((lead) => (
            <div key={lead.quote_id} className="bg-charcoal border border-crystal/20 rounded-xl p-5 hover:border-crystal/45 transition-colors duration-300" data-testid={`lead-card-${lead.quote_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h3 className="font-display text-white text-sm font-bold">{lead.client_name}</h3>
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border rounded-full px-2.5 py-0.5 ${STATUS_STYLE[lead.status] || STATUS_STYLE.new}`}>{lead.status}</span>
                  </div>
                  <p className="mt-1 text-xs text-ice/55">{lead.email}</p>
                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-venom border border-venom/40 rounded-full px-2.5 py-0.5">{lead.service}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#B983FF] border border-crystal/40 rounded-full px-2.5 py-0.5">{lead.budget}</span>
                    <span className="font-mono text-[10px] text-ice/40 px-1 py-0.5">{fmtDate(lead.created_at)}</span>
                    <EmailBadges status={lead.email_status} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {[["new", "New"], ["contacted", "Contacted"], ["closed", "Archive"]].map(([s, label]) => (
                    <button
                      key={s}
                      onClick={() => setStatus(lead.quote_id, s)}
                      disabled={lead.status === s}
                      className={`font-mono text-[10px] uppercase tracking-[0.1em] rounded-full px-3 py-1.5 border transition-all duration-300 disabled:opacity-30 ${
                        lead.status === s ? "border-white/10 text-ice/40" : "border-white/15 text-ice/70 hover:border-cyan-400/60 hover:text-cyan-300"
                      }`}
                      data-testid={`lead-status-${s}-${lead.quote_id}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {lead.details && (
                <div className="mt-3">
                  {expanded === lead.quote_id ? (
                    <div>
                      <p className="text-sm text-ice/70 leading-relaxed whitespace-pre-wrap border-l-2 border-crystal/50 pl-4">{lead.details}</p>
                      <button onClick={() => setExpanded(null)} className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.15em] text-ice/45 hover:text-ice" data-testid={`lead-collapse-${lead.quote_id}`}>
                        <X size={12} /> Hide notes
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setExpanded(lead.quote_id)} className="text-[11px] font-mono uppercase tracking-[0.15em] text-crystal hover:text-cyan-300 transition-colors" data-testid={`lead-expand-${lead.quote_id}`}>
                      View project notes →
                    </button>
                  )}
                </div>
              )}
              <ReplyBox path={`/quotes/${lead.quote_id}/reply`} testId={`lead-${lead.quote_id}`} thread={lead.thread} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState(null);

  const load = useCallback(async () => {
    const res = await jsonApi("/bookings");
    if (res.ok) setBookings((await res.json()).bookings || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="bookings-tab">
      <RefreshBtn onClick={load} testId="bookings-refresh-button" />
      <div className="mt-5 space-y-3" data-testid="bookings-list">
        {bookings === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-cyan-400" size={26} /></div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/12 rounded-2xl" data-testid="bookings-empty">
            <CalendarCheck size={30} className="mx-auto text-ice/25 mb-3" />
            <p className="text-sm text-ice/45">No bookings yet.</p>
          </div>
        ) : (
          bookings.map((b) => (
            <div key={b.booking_id} className="bg-charcoal border border-crystal/20 rounded-xl p-5" data-testid={`booking-card-${b.booking_id}`}>
              <div className="flex flex-wrap items-center gap-2.5">
                <h3 className="font-display text-white text-sm font-bold">{b.name}</h3>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border rounded-full px-2.5 py-0.5 ${STATUS_STYLE[b.status] || STATUS_STYLE.pending_payment}`}>
                  {b.status === "pending_payment" ? "pending payment" : b.status}
                </span>
                <span className="font-mono text-[10px] text-venom">${b.price}</span>
              </div>
              <p className="mt-1.5 text-xs text-ice/55">{b.email}</p>
              <p className="mt-1 text-xs text-ice/70">{b.label} · <span className="text-venom">{b.date} at {b.time_slot}</span></p>
              {b.notes && <p className="mt-2 text-xs text-ice/50 border-l-2 border-crystal/50 pl-3">{b.notes}</p>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const TRACK_EMPTY = { title: "", artist: "", bpm: "", key: "", mood: "", exclusive: false };

function TracksTab() {
  const [tracks, setTracks] = useState(null);
  const [form, setForm] = useState(TRACK_EMPTY);
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const res = await jsonApi("/tracks");
    if (res.ok) setTracks((await res.json()).tracks || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm(TRACK_EMPTY);
    setAudioFile(null);
    setCoverFile(null);
    setEditingId(null);
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ title: t.title, artist: t.artist, bpm: t.bpm ? String(t.bpm) : "", key: t.key || "", mood: t.mood || "", exclusive: !!t.exclusive });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    setUploading(true);
    try {
      if (editingId) {
        const payload = { title: form.title, artist: form.artist, key: form.key, mood: form.mood, exclusive: form.exclusive };
        if (form.bpm) payload.bpm = parseInt(form.bpm, 10);
        const res = await jsonApi(`/tracks/${editingId}`, { method: "PATCH", body: JSON.stringify(payload) });
        if (!res.ok) throw new Error("Update failed.");
        setMessage("Track updated.");
      } else {
        if (!audioFile) throw new Error("Choose an audio file first.");
        const fd = new FormData();
        fd.append("file", audioFile);
        if (coverFile) fd.append("cover", coverFile);
        Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
        const res = await api("/tracks", { method: "POST", body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed.");
        setMessage(`Uploaded "${data.track.title}" (${data.track.format}). It's live in the Beat Store — store plays a 40-second preview, buyers get the full file.`);
      }
      reset();
      e.target.reset();
      load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (trackId, title) => {
    if (!window.confirm(`Remove "${title}" from the Beat Store?`)) return;
    const res = await api(`/tracks/${trackId}`, { method: "DELETE" });
    if (res.ok) load();
  };

  return (
    <div data-testid="tracks-tab">
      <form onSubmit={submit} className="bg-charcoal border border-crystal/25 rounded-2xl p-6" data-testid="track-upload-form">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">
          {editingId ? "Edit Track" : "Upload a Track"}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <input required value={form.title} onChange={set("title")} placeholder="Track title *" className={inputCls} data-testid="track-input-title" />
          <input value={form.artist} onChange={set("artist")} placeholder="Artist (default: DGE Production Lab)" className={inputCls} data-testid="track-input-artist" />
          <input value={form.bpm} onChange={set("bpm")} placeholder="BPM" inputMode="numeric" className={inputCls} data-testid="track-input-bpm" />
          <input value={form.key} onChange={set("key")} placeholder="Key (e.g. C# min)" className={inputCls} data-testid="track-input-key" />
          <input value={form.mood} onChange={set("mood")} placeholder="Mood (e.g. Dark Trap)" className={inputCls} data-testid="track-input-mood" />
          <label className="flex items-center gap-2.5 border border-white/15 rounded-xl px-4 py-3 cursor-pointer hover:border-crystal/60 transition-colors" data-testid="track-input-exclusive-label">
            <input type="checkbox" checked={form.exclusive} onChange={(e) => setForm((f) => ({ ...f, exclusive: e.target.checked }))} className="accent-[#39FF14] w-4 h-4" data-testid="track-input-exclusive" />
            <span className="text-xs text-ice/70">Exclusive beat (sold once)</span>
          </label>
          {!editingId && (
            <div className="flex gap-3 sm:col-span-2 lg:col-span-3">
              <label className="flex-1 cursor-pointer border border-dashed border-white/20 rounded-xl px-3 py-3 text-xs text-ice/55 hover:border-cyan-400/60 hover:text-cyan-300 transition-colors" data-testid="track-audio-drop">
                <input type="file" accept="audio/*" className="hidden" onChange={(e) => setAudioFile(e.target.files[0] || null)} data-testid="track-input-audio" />
                {audioFile ? audioFile.name : "Audio file * (WAV/MP3) — store plays a 40s preview automatically"}
              </label>
              <label className="flex-1 cursor-pointer border border-dashed border-white/20 rounded-xl px-3 py-3 text-xs text-ice/55 hover:border-crystal/60 hover:text-crystal transition-colors" data-testid="track-cover-drop">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setCoverFile(e.target.files[0] || null)} data-testid="track-input-cover" />
                {coverFile ? coverFile.name : "Cover art (optional)"}
              </label>
            </div>
          )}
        </div>
        {message && <p className="mt-3 text-sm text-ice/70" data-testid="track-upload-message">{message}</p>}
        <div className="mt-5 flex items-center gap-3">
          <button type="submit" disabled={uploading} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all duration-300" data-testid="track-upload-submit">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {uploading ? "Working…" : editingId ? "Save Changes" : "Upload to Beat Store"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="text-xs font-mono uppercase tracking-[0.15em] text-ice/50 hover:text-white transition-colors" data-testid="track-edit-cancel">
              Cancel edit
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3" data-testid="tracks-list">
        {tracks === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
        ) : tracks.length === 0 ? (
          <p className="text-sm text-ice/45 text-center py-10">No tracks in the store.</p>
        ) : (
          tracks.map((t) => (
            <div key={t.id} className="flex items-center gap-4 bg-charcoal border border-crystal/20 rounded-xl p-4" data-testid={`admin-track-${t.id}`}>
              <img src={mediaUrl(t.cover)} alt="" className="w-12 h-12 rounded-lg object-cover border border-white/10" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-white text-sm font-bold truncate">
                  {t.title}
                  {t.exclusive && <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] text-[#B983FF] border border-crystal/50 rounded-full px-2 py-0.5">exclusive</span>}
                  {t.is_demo && <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] text-crystal border border-crystal/40 rounded-full px-2 py-0.5">demo</span>}
                  <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] text-venom border border-venom/40 rounded-full px-2 py-0.5">{t.format || "WAV"}</span>
                </p>
                <p className="text-[11px] text-ice/50">{t.artist}{t.bpm ? ` · ${t.bpm} BPM` : ""}{t.key ? ` · ${t.key}` : ""}</p>
              </div>
              <EditBtn onClick={() => startEdit(t)} label={`Edit ${t.title}`} testId={`track-edit-${t.id}`} />
              <DeleteBtn onClick={() => remove(t.id, t.title)} label={`Delete ${t.title}`} testId={`track-delete-${t.id}`} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const PRESS_EMPTY = { title: "", date: "", category: "", body: "", link_label: "", link_url: "" };

function PressTab() {
  const [articles, setArticles] = useState(null);
  const [form, setForm] = useState(PRESS_EMPTY);
  const [photo, setPhoto] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const res = await jsonApi("/press");
    if (res.ok) setArticles((await res.json()).articles || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => { setForm(PRESS_EMPTY); setPhoto(null); setEditingId(null); };

  const startEdit = (a) => {
    setEditingId(a.article_id);
    setForm({ title: a.title, date: a.date, category: a.category, body: a.body, link_label: a.link_label || "", link_url: a.link_url || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSaving(true);
    try {
      if (editingId) {
        const res = await jsonApi(`/press/${editingId}`, { method: "PATCH", body: JSON.stringify(form) });
        if (!res.ok) throw new Error("Update failed.");
        setMessage("Article updated.");
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        if (photo) fd.append("photo", photo);
        const res = await api("/press", { method: "POST", body: fd });
        if (!res.ok) throw new Error("Publish failed.");
        setMessage("Article published to the Press Room.");
      }
      reset();
      e.target.reset();
      load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete article "${title}"?`)) return;
    const res = await api(`/press/${id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  return (
    <div data-testid="press-tab">
      <form onSubmit={submit} className="bg-charcoal border border-crystal/25 rounded-2xl p-6" data-testid="press-form">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">
          {editingId ? "Edit Press Article" : "Publish Press / News"}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <input required value={form.title} onChange={set("title")} placeholder="Headline *" className={inputCls} data-testid="press-input-title" />
          <input required value={form.date} onChange={set("date")} placeholder="Date (e.g. September 2026) *" className={inputCls} data-testid="press-input-date" />
          <input value={form.category} onChange={set("category")} placeholder="Category (e.g. Awards & Recognition)" className={inputCls} data-testid="press-input-category" />
          <label className="cursor-pointer border border-dashed border-white/20 rounded-xl px-4 py-3 text-xs text-ice/55 hover:border-cyan-400/60 hover:text-cyan-300 transition-colors" data-testid="press-photo-drop">
            <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="press-input-photo" />
            {photo ? photo.name : "News photo (optional)"}
          </label>
          <textarea required rows={4} value={form.body} onChange={set("body")} placeholder="Article body *" className={`${inputCls} resize-none sm:col-span-2`} data-testid="press-input-body" />
          <input value={form.link_label} onChange={set("link_label")} placeholder="Link text (e.g. Read Full Article)" className={inputCls} data-testid="press-input-link-label" />
          <input value={form.link_url} onChange={set("link_url")} placeholder="Link URL (e.g. /beats or https://…)" className={inputCls} data-testid="press-input-link-url" />
        </div>
        {message && <p className="mt-3 text-sm text-ice/70" data-testid="press-message">{message}</p>}
        <div className="mt-5 flex items-center gap-3">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all duration-300" data-testid="press-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Newspaper size={16} />}
            {saving ? "Working…" : editingId ? "Save Changes" : "Publish Article"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="text-xs font-mono uppercase tracking-[0.15em] text-ice/50 hover:text-white" data-testid="press-edit-cancel">Cancel edit</button>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3" data-testid="press-list">
        {articles === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
        ) : articles.length === 0 ? (
          <p className="text-sm text-ice/45 text-center py-10">No articles yet.</p>
        ) : (
          articles.map((a) => (
            <div key={a.article_id} className="flex items-start gap-4 bg-charcoal border border-crystal/20 rounded-xl p-4" data-testid={`press-item-${a.article_id}`}>
              {a.photo && <img src={mediaUrl(a.photo)} alt="" className="w-14 h-14 rounded-lg object-cover border border-white/10 shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="font-display text-white text-sm font-bold">{a.title}</p>
                <p className="text-[11px] text-ice/50 mt-1">{a.date}{a.category ? ` · ${a.category}` : ""}</p>
              </div>
              <EditBtn onClick={() => startEdit(a)} label={`Edit ${a.title}`} testId={`press-edit-${a.article_id}`} />
              <DeleteBtn onClick={() => remove(a.article_id, a.title)} label={`Delete ${a.title}`} testId={`press-delete-${a.article_id}`} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const MERCH_EMPTY = { name: "", tag: "", price: "", store_url: "" };

function MerchTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(MERCH_EMPTY);
  const [image, setImage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const res = await jsonApi("/merch");
    if (res.ok) setItems((await res.json()).items || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => { setForm(MERCH_EMPTY); setImage(null); setEditingId(null); };

  const startEdit = (m) => {
    setEditingId(m.m_id);
    setForm({ name: m.name, tag: m.tag, price: m.price, store_url: m.store_url || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSaving(true);
    try {
      if (editingId) {
        const res = await jsonApi(`/merch/${editingId}`, { method: "PATCH", body: JSON.stringify(form) });
        if (!res.ok) throw new Error("Update failed.");
        setMessage("Item updated.");
      } else {
        if (!image) throw new Error("Product image is required.");
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append("image", image);
        const res = await api("/merch", { method: "POST", body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Save failed.");
        setMessage("Product added to the Merch section.");
      }
      reset();
      e.target.reset();
      load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the merch grid?`)) return;
    const res = await api(`/merch/${id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  const editingItem = items?.find((m) => m.m_id === editingId);

  const addGallery = async (files) => {
    if (!files?.length || !editingId) return;
    setGalleryBusy(true);
    setMessage("");
    const fd = new FormData();
    [...files].forEach((f) => fd.append("images", f));
    const res = await api(`/merch/${editingId}/gallery`, { method: "POST", body: fd });
    const data = await res.json().catch(() => ({}));
    setMessage(res.ok ? "Gallery photos added." : (typeof data.detail === "string" ? data.detail : "Upload failed."));
    setGalleryBusy(false);
    load();
  };

  const delGallery = async (idx) => {
    const res = await api(`/merch/${editingId}/gallery/${idx}`, { method: "DELETE" });
    if (res.ok) load();
  };

  return (
    <div data-testid="merch-tab">
      <form onSubmit={submit} className="bg-charcoal border border-crystal/25 rounded-2xl p-6" data-testid="merch-form">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">
          {editingId ? "Edit Product" : "Add Merch Product"}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <input required value={form.name} onChange={set("name")} placeholder="Product name *" className={inputCls} data-testid="merch-input-name" />
          <input value={form.tag} onChange={set("tag")} placeholder="Tag (e.g. Custom Apparel)" className={inputCls} data-testid="merch-input-tag" />
          <input value={form.price} onChange={set("price")} placeholder="Price label (e.g. From $45)" className={inputCls} data-testid="merch-input-price" />
          <input value={form.store_url} onChange={set("store_url")} placeholder="This item's link (photo + button go here)" className={inputCls} data-testid="merch-input-url" />
          {!editingId && (
            <label className="cursor-pointer border border-dashed border-white/20 rounded-xl px-4 py-3 text-xs text-ice/55 hover:border-cyan-400/60 hover:text-cyan-300 transition-colors sm:col-span-2" data-testid="merch-image-drop">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setImage(e.target.files[0] || null)} data-testid="merch-input-image" />
              {image ? image.name : "Product image * (JPG/PNG/WebP)"}
            </label>
          )}
        </div>
        {editingId && editingItem && (
          <div className="mt-4 border-t border-white/10 pt-4" data-testid="merch-gallery-manager">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ice/50 mb-3">
              Photo gallery — swipeable on the product card ({(editingItem.gallery || []).length} extra photos)
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {(editingItem.gallery || []).map((g, gi) => (
                <div key={gi} className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/15 group/g">
                  <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => delGallery(gi)}
                    aria-label={`Remove photo ${gi + 1}`}
                    className="absolute inset-0 bg-obsidian/70 text-red-400 opacity-0 group-hover/g:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid={`merch-gallery-delete-${gi}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <label className="w-16 h-16 rounded-lg border border-dashed border-white/20 flex items-center justify-center text-ice/45 hover:border-cyan-400/60 hover:text-cyan-300 cursor-pointer transition-colors text-xl leading-none" data-testid="merch-gallery-add">
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addGallery(e.target.files); e.target.value = ""; }} data-testid="merch-gallery-input" />
                {galleryBusy ? <Loader2 size={16} className="animate-spin" /> : "+"}
              </label>
            </div>
          </div>
        )}
        {message && <p className="mt-3 text-sm text-ice/70" data-testid="merch-message">{message}</p>}
        <div className="mt-5 flex items-center gap-3">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all duration-300" data-testid="merch-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
            {saving ? "Working…" : editingId ? "Save Changes" : "Add Product"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="text-xs font-mono uppercase tracking-[0.15em] text-ice/50 hover:text-white" data-testid="merch-edit-cancel">Cancel edit</button>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3" data-testid="merch-list">
        {items === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-ice/45 text-center py-10">No merch items yet.</p>
        ) : (
          items.map((m) => (
            <div key={m.m_id} className="flex items-center gap-4 bg-charcoal border border-crystal/20 rounded-xl p-4" data-testid={`merch-item-${m.m_id}`}>
              <img src={mediaUrl(m.image)} alt="" className="w-12 h-12 rounded-lg object-cover border border-white/10" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-white text-sm font-bold truncate">{m.name}</p>
                <p className="text-[11px] text-ice/50">{m.tag}{m.price ? ` · ${m.price}` : ""}</p>
              </div>
              <EditBtn onClick={() => startEdit(m)} label={`Edit ${m.name}`} testId={`merch-edit-${m.m_id}`} />
              <DeleteBtn onClick={() => remove(m.m_id, m.name)} label={`Delete ${m.name}`} testId={`merch-delete-${m.m_id}`} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const REVIEW_EMPTY = { quote: "", name: "", role: "" };

function ReviewsTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(REVIEW_EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const res = await jsonApi("/testimonials?all=1");
    if (res.ok) setItems((await res.json()).testimonials || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => { setForm(REVIEW_EMPTY); setEditingId(null); };

  const approve = async (t) => {
    const res = await jsonApi(`/testimonials/${t.t_id}`, {
      method: "PATCH",
      body: JSON.stringify({ quote: t.quote, name: t.name, role: t.role, approved: true }),
    });
    if (res.ok) {
      setMessage(`Review from ${t.name} is now live on the site.`);
      load();
    }
  };

  const attachPhoto = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("photo", file);
    const res = await api(`/testimonials/${id}/photo`, { method: "POST", body: fd });
    if (res.ok) {
      setMessage("Photo attached — it shows on the homepage with this review and on the fan mail wall.");
      load();
    } else {
      setMessage("Photo upload failed.");
    }
  };

  const startEdit = (t) => {
    setEditingId(t.t_id);
    setForm({ quote: t.quote, name: t.name, role: t.role });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSaving(true);
    try {
      const res = editingId
        ? await jsonApi(`/testimonials/${editingId}`, { method: "PATCH", body: JSON.stringify(form) })
        : await jsonApi("/testimonials", { method: "POST", body: JSON.stringify(form) });
      if (!res.ok) throw new Error("Save failed.");
      setMessage(editingId ? "Review updated." : "Review added to the homepage carousel.");
      reset();
      load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove review from ${name}?`)) return;
    const res = await api(`/testimonials/${id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  return (
    <div data-testid="reviews-tab">
      <form onSubmit={submit} className="bg-charcoal border border-crystal/25 rounded-2xl p-6" data-testid="review-form">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">
          {editingId ? "Edit Review" : "Add Artist Review"}
        </p>
        <div className="grid gap-4">
          <textarea required rows={3} value={form.quote} onChange={set("quote")} placeholder="Artist quote *" className={`${inputCls} resize-none`} data-testid="review-input-quote" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={set("name")} placeholder="Artist name *" className={inputCls} data-testid="review-input-name" />
            <input value={form.role} onChange={set("role")} placeholder="Role (e.g. R&B Artist · Beat Licensing)" className={inputCls} data-testid="review-input-role" />
          </div>
        </div>
        {message && <p className="mt-3 text-sm text-ice/70" data-testid="review-message">{message}</p>}
        <div className="mt-5 flex items-center gap-3">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all duration-300" data-testid="review-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Quote size={16} />}
            {saving ? "Working…" : editingId ? "Save Changes" : "Add Review"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="text-xs font-mono uppercase tracking-[0.15em] text-ice/50 hover:text-white" data-testid="review-edit-cancel">Cancel edit</button>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3" data-testid="reviews-list">
        {items === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-ice/45 text-center py-10">No reviews yet.</p>
        ) : (
          items.map((t) => (
            <div key={t.t_id} className="flex items-start gap-4 bg-charcoal border border-crystal/20 rounded-xl p-4" data-testid={`review-item-${t.t_id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-3">
                  {t.photo && (
                    <img src={mediaUrl(t.photo)} alt="" className="w-11 h-11 rounded-lg object-cover border border-white/15 shrink-0" data-testid={`review-photo-thumb-${t.t_id}`} />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-ice/75 leading-relaxed line-clamp-2">&ldquo;{t.quote}&rdquo;</p>
                    <p className="text-[11px] text-ice/50 mt-1.5">{t.name}{t.role ? ` · ${t.role}` : ""}</p>
                  </div>
                </div>
                {t.approved === false && (
                  <span className="mt-2 inline-block font-mono text-[9px] uppercase tracking-[0.15em] text-crystal border border-crystal/50 rounded-full px-2.5 py-0.5" data-testid={`review-pending-${t.t_id}`}>
                    Pending approval — hidden from site
                  </span>
                )}
              </div>
              {t.approved === false && (
                <button
                  onClick={() => approve(t)}
                  className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-venom/50 text-venom rounded-full px-3.5 py-1.5 hover:bg-[#A855F7] hover:text-white transition-all"
                  data-testid={`review-approve-${t.t_id}`}
                >
                  <CheckCheck size={13} /> Approve
                </button>
              )}
              <label className="p-2 text-ice/40 hover:text-cyan-300 transition-colors cursor-pointer shrink-0" aria-label={`Attach fan letter photo for ${t.name}`} data-testid={`review-photo-${t.t_id}`}>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { attachPhoto(t.t_id, e.target.files[0]); e.target.value = ""; }} data-testid={`review-photo-input-${t.t_id}`} />
                <ImagePlus size={16} />
              </label>
              <EditBtn onClick={() => startEdit(t)} label={`Edit ${t.name}`} testId={`review-edit-${t.t_id}`} />
              <DeleteBtn onClick={() => remove(t.t_id, t.name)} label={`Delete ${t.name}`} testId={`review-delete-${t.t_id}`} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ApplicationsTab({ endpoint, idField, nameField, fields, statuses, statusLabels = {}, emptyLabel, testPrefix, description, replies = [], positionsManager = false, photoSupport = false, interviewable = false, ghostSupport = false }) {
  const [items, setItems] = useState(null);
  const [message, setMessage] = useState("");
  const [positions, setPositions] = useState(null);
  const [newPosition, setNewPosition] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const ARCHIVED_STATUSES = ["closed", "passed"];

  const load = useCallback(async () => {
    const res = await jsonApi(`/${endpoint}`);
    if (res.ok) setItems((await res.json()).items || []);
    if (positionsManager) {
      const pr = await jsonApi(`/${endpoint}/positions`);
      if (pr.ok) setPositions((await pr.json()).positions || []);
    }
  }, [endpoint, positionsManager]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    const res = await jsonApi(`/${endpoint}/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    if (res.ok) load();
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete the application from ${name}? This cannot be undone.`)) return;
    const res = await api(`/${endpoint}/${id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  const sendReply = async (id, template) => {
    setReplyingTo(`${id}-${template}`);
    setMessage("");
    const res = await jsonApi(`/${endpoint}/${id}/reply`, { method: "POST", body: JSON.stringify({ template }) });
    const data = await res.json().catch(() => ({}));
    setMessage(res.ok ? `${template.replace(/_/g, " ")} email sent to ${data.to}.` : (typeof data.detail === "string" ? data.detail : "Reply failed."));
    setReplyingTo(null);
    if (res.ok) load();
  };

  const addPosition = async (e) => {
    e.preventDefault();
    if (!newPosition.trim()) return;
    const res = await jsonApi(`/${endpoint}/positions`, { method: "POST", body: JSON.stringify({ title: newPosition.trim() }) });
    if (res.ok) { setNewPosition(""); load(); }
  };

  const archivePosition = async (pId) => {
    const res = await api(`/${endpoint}/positions/${pId}`, { method: "DELETE" });
    if (res.ok) load();
  };

  const fieldValue = (v) => (typeof v === "boolean" ? (v ? "Yes" : "No") : v);

  const attachRosterPhoto = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("photo", file);
    const res = await api(`/${endpoint}/${id}/photo`, { method: "POST", body: fd });
    if (res.ok) {
      setMessage("Roster photo set — it appears on the public Roster page when this artist is A&R Approved.");
      load();
    } else {
      setMessage("Photo upload failed.");
    }
  };

  const toggleGhost = async (id, on) => {
    const res = await jsonApi(`/${endpoint}/${id}/ghost`, { method: "POST", body: JSON.stringify({ ghost: on }) });
    setMessage(res.ok
      ? (on ? "Featured as a Ghost Verse — it's now playable in the Medusa Room Snake Pit." : "Ghost Verse pulled from the Medusa Room.")
      : "Ghost toggle failed.");
    if (res.ok) load();
  };

  return (
    <div data-testid={`${testPrefix}-tab`}>
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">{description}</p>

      {positionsManager && (
        <div className="mb-6 bg-charcoal border border-crystal/25 rounded-2xl p-5" data-testid="positions-manager">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-3">Open Positions (shown on the Careers page)</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {(positions || []).map((p) => (
              <span key={p.p_id} className="inline-flex items-center gap-2 text-xs text-ice/75 border border-white/15 rounded-full pl-3.5 pr-1.5 py-1.5" data-testid={`position-chip-${p.p_id}`}>
                {p.title}
                <button onClick={() => archivePosition(p.p_id)} aria-label={`Archive ${p.title}`} className="w-5 h-5 rounded-full bg-white/8 text-ice/50 hover:text-red-400 hover:bg-red-500/15 flex items-center justify-center transition-colors" data-testid={`position-archive-${p.p_id}`}>
                  <X size={11} />
                </button>
              </span>
            ))}
            {positions && positions.length === 0 && <span className="text-xs text-ice/40">No open positions — the form will show a general application.</span>}
          </div>
          <form onSubmit={addPosition} className="flex gap-2">
            <input value={newPosition} onChange={(e) => setNewPosition(e.target.value)} placeholder="Post a new opening (e.g. Licensing Coordinator)" className={`${inputCls} flex-1`} data-testid="position-input" />
            <button type="submit" className="shrink-0 inline-flex items-center gap-1.5 bg-[#A855F7] text-white font-bold text-xs px-4 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 transition-all" data-testid="position-add">
              Post
            </button>
          </form>
        </div>
      )}

      {message && <p className="mb-4 text-sm text-ice/70" data-testid={`${testPrefix}-message`}>{message}</p>}
      {items && items.some((i) => ARCHIVED_STATUSES.includes(i.status)) && (
        <div className="mb-4">
          <button onClick={() => setShowArchived(!showArchived)} className="text-[11px] font-mono uppercase tracking-[0.15em] border border-white/15 text-ice/60 rounded-full px-4 py-2 hover:border-cyan-400/60 hover:text-cyan-300 transition-all" data-testid={`${testPrefix}-archived-toggle`}>
            {showArchived ? "Hide archived" : `Show archived (${items.filter((i) => ARCHIVED_STATUSES.includes(i.status)).length})`}
          </button>
        </div>
      )}
      {items === null ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
      ) : items.filter((i) => showArchived || !ARCHIVED_STATUSES.includes(i.status)).length === 0 ? (
        <p className="text-sm text-ice/45 text-center py-10" data-testid={`${testPrefix}-empty`}>{emptyLabel}</p>
      ) : (
        <div className="space-y-3">
          {items.filter((i) => showArchived || !ARCHIVED_STATUSES.includes(i.status)).map((item) => {
            const id = item[idField];
            return (
              <div key={id} className="bg-charcoal border border-crystal/20 rounded-xl p-5" data-testid={`${testPrefix}-card-${id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <p className="font-display text-white text-sm font-bold">{item[nameField]}</p>
                  <span className="text-[11px] text-ice/50">{item.email}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1 border ${STATUS_STYLE[item.status] || STATUS_STYLE.new}`} data-testid={`${testPrefix}-status-${id}`}>
                    {statusLabels[item.status] || item.status}
                  </span>
                  <span className="text-[10px] text-ice/35 font-mono">{new Date(item.created_at).toLocaleDateString()}</span>
                  <EmailBadges status={item.email_status} labels={["owner", "applicant"]} />
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {fields.map(([label, key]) => (
                    item[key] ? (
                      <p key={key} className="text-xs text-ice/60">
                        <span className="text-crystal font-mono text-[10px] uppercase tracking-[0.1em]">{label}: </span>
                        {fieldValue(item[key])}
                      </p>
                    ) : null
                  ))}
                </div>
                {item.message && <p className="mt-2.5 text-xs text-ice/55 leading-relaxed whitespace-pre-line border-t border-white/8 pt-2.5">{item.message}</p>}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <select
                    value={item.status}
                    onChange={(e) => setStatus(id, e.target.value)}
                    className="bg-obsidian border border-white/15 rounded-full px-3 py-1.5 text-[11px] text-ice focus:outline-none focus:border-venom/60"
                    data-testid={`${testPrefix}-status-select-${id}`}
                  >
                    {statuses.map((s) => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
                  </select>
                  {item.demo_path && (
                    <span className="inline-flex items-center gap-2 border border-cyan-400/40 rounded-full px-3 py-1.5" data-testid={`${testPrefix}-demo-${id}`}>
                      <FileAudio size={13} className="text-cyan-300" />
                      <audio controls preload="none" src={`${BACKEND}/api/admin-files/${item.demo_path}`} className="h-7 w-44 sm:w-56" />
                    </span>
                  )}
                  {ghostSupport && item.demo_path && (
                    <button onClick={() => toggleGhost(id, !item.ghost)}
                      className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] rounded-full px-3.5 py-1.5 border transition-all ${item.ghost ? "border-teal-400 text-teal-300 bg-teal-500/10" : "border-white/15 text-ice/60 hover:border-teal-400/60 hover:text-teal-300"}`}
                      data-testid={`${testPrefix}-ghost-${id}`}>
                      <FileAudio size={12} /> {item.ghost ? "Ghost Verse ✓ Live" : "Feature as Ghost Verse"}
                    </button>
                  )}
                  {item.resume_path && (
                    <a href={`${BACKEND}/api/admin-files/${item.resume_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-crystal/40 text-[#B983FF] rounded-full px-3.5 py-1.5 hover:bg-crystal hover:text-obsidian transition-all" data-testid={`${testPrefix}-resume-${id}`}>
                      <FileText size={13} /> {item.resume_name || "Resume"}
                    </a>
                  )}
                  {photoSupport && (
                    <label className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-white/15 text-ice/60 rounded-full px-3.5 py-1.5 hover:border-cyan-400/60 hover:text-cyan-300 cursor-pointer transition-all" data-testid={`${testPrefix}-photo-${id}`}>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { attachRosterPhoto(id, e.target.files[0]); e.target.value = ""; }} data-testid={`${testPrefix}-photo-input-${id}`} />
                      <ImagePlus size={12} /> {item.roster_photo ? "Change Photo" : "Roster Photo"}
                    </label>
                  )}
                  {replies.map(([tpl, label]) => (
                    <button
                      key={tpl}
                      onClick={() => sendReply(id, tpl)}
                      disabled={replyingTo === `${id}-${tpl}`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-white/15 text-ice/60 rounded-full px-3.5 py-1.5 hover:border-cyan-400/60 hover:text-cyan-300 disabled:opacity-50 transition-all"
                      data-testid={`${testPrefix}-reply-${tpl}-${id}`}
                    >
                      {replyingTo === `${id}-${tpl}` ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      {label}
                    </button>
                  ))}
                  <DeleteBtn onClick={() => remove(id, item[nameField])} label={`Delete application from ${item[nameField]}`} testId={`${testPrefix}-delete-${id}`} />
                </div>
                <ReplyBox path={`/${endpoint}/${id}/message`} testId={`${testPrefix}-${id}`} thread={item.thread} />
                {interviewable && (
                  <div className="w-full mt-3 flex flex-wrap items-center gap-2" data-testid={`${testPrefix}-interview-${id}`}>
                    <CalendarClock size={14} className="text-cyan-300" />
                    <input type="datetime-local" defaultValue={item.interview_at || ""} className="bg-[#14141A] border border-[#282836] rounded-lg px-3 py-1.5 text-xs text-ice [color-scheme:dark] focus:outline-none focus:border-cyan-400/60" data-testid={`${testPrefix}-interview-at-${id}`} id={`iv-${id}`} />
                    <input placeholder="Interview notes (location, link…)" defaultValue={item.interview_notes || ""} className="bg-[#14141A] border border-[#282836] rounded-lg px-3 py-1.5 text-xs text-ice flex-1 min-w-40 focus:outline-none focus:border-cyan-400/60" data-testid={`${testPrefix}-interview-notes-${id}`} id={`ivn-${id}`} />
                    <button onClick={async () => {
                      const at = document.getElementById(`iv-${id}`).value;
                      const notes = document.getElementById(`ivn-${id}`).value;
                      const res = await jsonApi(`/${endpoint}/${id}`, { method: "PATCH", body: JSON.stringify({ interview_at: at, interview_notes: notes }) });
                      if (res.ok) { setMessage("Interview scheduled."); load(); }
                    }} className="text-[11px] font-mono uppercase tracking-[0.12em] border border-[#A855F7]/50 text-purple-300 rounded-full px-3.5 py-1.5 hover:bg-[#A855F7] hover:text-white transition-all" data-testid={`${testPrefix}-interview-save-${id}`}>Save</button>
                    {item.interview_at && <span className="font-mono text-[10px] text-cyan-300">Scheduled: {new Date(item.interview_at).toLocaleString()}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PITCH_EMPTY = { outlet: "", subject: "", body: "", status: "draft" };

function PitchesTab() {
  const [pitches, setPitches] = useState(null);
  const [form, setForm] = useState(PITCH_EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const res = await jsonApi("/pitches");
    if (res.ok) setPitches((await res.json()).pitches || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startEdit = (p) => {
    setEditingId(p.p_id);
    setForm({ outlet: p.outlet, subject: p.subject, body: p.body, status: p.status });
  };

  const reset = () => { setForm(PITCH_EMPTY); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSaving(true);
    try {
      const url = editingId ? `/pitches/${editingId}` : "/pitches";
      const res = await jsonApi(url, { method: editingId ? "PATCH" : "POST", body: JSON.stringify(form) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Save failed.");
      setMessage(editingId ? "Pitch updated." : "Pitch saved.");
      reset();
      load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const draftWithAI = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    setMessage("");
    try {
      const context = `Draft a press pitch email to ${form.outlet || "a local arts & entertainment editor"} about: ${form.subject || "Heather Da Boa / DGE: Cold-Blooded Records"}. Include: subject-ready hook, short artist intro (indie R&B/Hip-Hop artist, songwriter, CEO; #1 Local & #1 Regional ReverbNation), the pitch ask (feature/interview/roundup), links line (cold-blooded-records.com/news), genre line, and a warm sign-off as Heather Parrish, CEO, with phone (803) 900-2564 and media.press@cold-blooded-records.com.`;
      await streamSSE(`${API}/ai/write`, { kind: "press", context }, (d) => setForm((f) => ({ ...f, body: f.body + d })));
    } catch (err) {
      setMessage(err.message);
    } finally {
      setAiBusy(false);
    }
  };

  const copy = async (p) => {
    try {
      await navigator.clipboard.writeText(`Subject: ${p.subject}\n\n${p.body}`);
      setMessage("Pitch copied to clipboard.");
    } catch {
      setMessage("Select the text and copy it manually.");
    }
  };

  const toggleStatus = async (p) => {
    const res = await jsonApi(`/pitches/${p.p_id}`, {
      method: "PATCH",
      body: JSON.stringify({ ...p, status: p.status === "sent" ? "draft" : "sent" }),
    });
    if (res.ok) load();
  };

  const remove = async (id, subject) => {
    if (!window.confirm(`Delete pitch "${subject || "Untitled"}"?`)) return;
    const res = await api(`/pitches/${id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  return (
    <div data-testid="pitches-tab">
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">
        Saved press pitches for editors and media outlets. Draft new ones with the AI writer, copy them out, and mark them sent.
      </p>

      <form onSubmit={submit} className="bg-charcoal border border-crystal/25 rounded-2xl p-6" data-testid="pitch-form">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
            {editingId ? "Edit Pitch" : "New Pitch"}
          </p>
          <button type="button" onClick={draftWithAI} disabled={aiBusy} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-crystal/50 text-[#B983FF] rounded-full px-4 py-2 hover:bg-crystal hover:text-obsidian disabled:opacity-50 transition-all" data-testid="pitch-ai-draft">
            {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {aiBusy ? "Drafting…" : "Draft with AI"}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input value={form.outlet} onChange={set("outlet")} placeholder="Outlet (e.g. The Post and Courier)" className={inputCls} data-testid="pitch-input-outlet" />
          <input value={form.subject} onChange={set("subject")} placeholder="Email subject line" className={inputCls} data-testid="pitch-input-subject" />
          <textarea rows={8} value={form.body} onChange={set("body")} placeholder="Pitch body…" className={`${inputCls} resize-y sm:col-span-2 font-mono text-[13px]`} data-testid="pitch-input-body" />
        </div>
        {message && <p className="mt-3 text-sm text-ice/70" data-testid="pitch-message">{message}</p>}
        <div className="mt-5 flex items-center gap-3">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all" data-testid="pitch-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Megaphone size={16} />}
            {saving ? "Working…" : editingId ? "Save Changes" : "Save Pitch"}
          </button>
          {editingId && <button type="button" onClick={reset} className="text-xs font-mono uppercase tracking-[0.15em] text-ice/50 hover:text-white transition-colors" data-testid="pitch-edit-cancel">Cancel edit</button>}
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {pitches === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
        ) : pitches.length === 0 ? (
          <p className="text-sm text-ice/45 text-center py-10" data-testid="pitches-empty">No pitches saved yet.</p>
        ) : (
          pitches.map((p) => (
            <div key={p.p_id} className="bg-charcoal border border-crystal/20 rounded-xl p-5" data-testid={`pitch-card-${p.p_id}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <p className="font-display text-white text-sm font-bold">{p.outlet || "No outlet"}</p>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1 border ${p.status === "sent" ? "bg-venom/15 text-venom border-venom/50" : "bg-white/8 text-ice/60 border-white/20"}`} data-testid={`pitch-status-${p.p_id}`}>
                  {p.status}
                </span>
                <span className="text-[10px] text-ice/35 font-mono">updated {new Date(p.updated_at).toLocaleDateString()}</span>
              </div>
              <p className="mt-2 text-xs text-crystal/90 font-medium">{p.subject}</p>
              <p className="mt-1.5 text-xs text-ice/55 leading-relaxed line-clamp-3 whitespace-pre-line">{p.body}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button onClick={() => copy(p)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-white/15 text-ice/60 rounded-full px-3.5 py-1.5 hover:border-cyan-400/60 hover:text-cyan-300 transition-all" data-testid={`pitch-copy-${p.p_id}`}>
                  <Copy size={12} /> Copy
                </button>
                <button onClick={() => toggleStatus(p)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-white/15 text-ice/60 rounded-full px-3.5 py-1.5 hover:border-cyan-400/60 hover:text-cyan-300 transition-all" data-testid={`pitch-toggle-${p.p_id}`}>
                  {p.status === "sent" ? <X size={12} /> : <CheckCheck size={12} />}
                  {p.status === "sent" ? "Mark Draft" : "Mark Sent"}
                </button>
                <EditBtn onClick={() => startEdit(p)} label="Edit pitch" testId={`pitch-edit-${p.p_id}`} />
                <DeleteBtn onClick={() => remove(p.p_id, p.subject)} label="Delete pitch" testId={`pitch-delete-${p.p_id}`} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const ORDER_EMPTY = { customer_name: "", customer_email: "", items: "", provider: "the33rd", external_ref: "", send_confirmation: true };const SHIP_EMPTY = { carrier: "usps", tracking_number: "", tracking_url: "" };

const ORDER_STATUS_STYLE = {
  pending: "bg-white/8 text-ice/60 border-white/20",
  processing: "bg-crystal/15 text-[#B983FF] border-crystal/50",
  shipped: "bg-venom/15 text-venom border-venom/50",
  delivered: "bg-venom/25 text-venom border-venom/70",
  canceled: "bg-red-500/10 text-red-400 border-red-500/40",
};

const MailBadge = ({ sent, label, testId }) => (
  <span className={`font-mono text-[9px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5 border ${sent ? "text-venom border-venom/40" : "text-ice/35 border-white/15"}`} data-testid={testId}>
    {label} {sent ? "✓" : "—"}
  </span>
);

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [form, setForm] = useState(ORDER_EMPTY);
  const [shipFor, setShipFor] = useState(null);
  const [ship, setShip] = useState(SHIP_EMPTY);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const res = await jsonApi("/merch-orders");
    if (res.ok) setOrders((await res.json()).orders || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSaving(true);
    try {
      const res = await jsonApi("/merch-orders", { method: "POST", body: JSON.stringify(form) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Save failed.");
      setMessage(form.send_confirmation
        ? `Order logged and confirmation email sent to ${data.order.customer_email}.`
        : "Order logged (no confirmation email sent).");
      setForm(ORDER_EMPTY);
      load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const patch = async (id, body, okMsg) => {
    const res = await jsonApi(`/merch-orders/${id}`, { method: "PATCH", body: JSON.stringify(body) });
    if (res.ok) {
      setMessage(okMsg);
      setShipFor(null);
      setShip(SHIP_EMPTY);
      load();
    } else {
      const data = await res.json().catch(() => ({}));
      setMessage(typeof data.detail === "string" ? data.detail : "Update failed.");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete the order for ${name}?`)) return;
    const res = await api(`/merch-orders/${id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  return (
    <div data-testid="orders-tab">
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">
        Log merch orders from any store (the33rd.store, Printify, Printful, in-person) and keep customers in the loop —
        confirmation, shipped-with-tracking and delivered emails go out automatically as you update the status.
      </p>

      <form onSubmit={submit} className="bg-charcoal border border-crystal/25 rounded-2xl p-6" data-testid="order-form">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">Log a Merch Order</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <input required value={form.customer_name} onChange={set("customer_name")} placeholder="Customer name *" className={inputCls} data-testid="order-input-name" />
          <input required type="email" value={form.customer_email} onChange={set("customer_email")} placeholder="Customer email *" className={inputCls} data-testid="order-input-email" />
          <select value={form.provider} onChange={set("provider")} className={inputCls} data-testid="order-input-provider">
            <option value="the33rd">the33rd.store</option>
            <option value="printify">Printify</option>
            <option value="printful">Printful</option>
            <option value="other">Other / In-person</option>
          </select>
          <input value={form.external_ref} onChange={set("external_ref")} placeholder="External order # (optional)" className={inputCls} data-testid="order-input-ref" />
          <textarea required rows={2} value={form.items} onChange={set("items")} placeholder={"Items ordered * (one per line)\ne.g. Slither N Sparkle™ Tee — Black / L"} className={`${inputCls} resize-none sm:col-span-2`} data-testid="order-input-items" />
          <label className="flex items-center gap-2.5 text-xs text-ice/60 cursor-pointer sm:col-span-2" data-testid="order-confirmation-label">
            <input type="checkbox" checked={form.send_confirmation} onChange={(e) => setForm((f) => ({ ...f, send_confirmation: e.target.checked }))} className="accent-[#39FF14] w-4 h-4" data-testid="order-input-confirmation" />
            Send the customer an order confirmation email now
          </label>
        </div>
        {message && <p className="mt-3 text-sm text-ice/70" data-testid="order-message">{message}</p>}
        <div className="mt-5">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all duration-300" data-testid="order-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Package size={16} />}
            {saving ? "Working…" : "Log Order"}
          </button>
        </div>
      </form>

      <div className="mt-6 space-y-3" data-testid="orders-list">
        {orders === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
        ) : orders.length === 0 ? (
          <p className="text-sm text-ice/45 text-center py-10" data-testid="orders-empty">No merch orders logged yet.</p>
        ) : (
          orders.map((o) => (
            <div key={o.order_id} className="bg-charcoal border border-crystal/20 rounded-xl p-5" data-testid={`order-card-${o.order_id}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <p className="font-display text-white text-sm font-bold">{o.customer_name}</p>
                <span className="text-[11px] text-ice/50">{o.customer_email}</span>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1 border ${ORDER_STATUS_STYLE[o.status] || ORDER_STATUS_STYLE.pending}`} data-testid={`order-status-${o.order_id}`}>
                  {o.status}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-crystal border border-crystal/40 rounded-full px-2.5 py-0.5">{o.provider}</span>
              </div>
              <p className="mt-2 text-xs text-ice/60 whitespace-pre-line">{o.items}</p>
              {o.external_ref && <p className="mt-1 text-[11px] text-ice/40 font-mono">Ref: {o.external_ref}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <MailBadge sent={o.confirmation_sent} label="Confirmation" testId={`order-email-confirmation-${o.order_id}`} />
                <MailBadge sent={o.shipped_email_sent} label="Shipped" testId={`order-email-shipped-${o.order_id}`} />
                <MailBadge sent={o.delivered_email_sent} label="Delivered" testId={`order-email-delivered-${o.order_id}`} />
              </div>
              {o.tracking_number && (
                <p className="mt-2 text-[11px] text-ice/50 font-mono" data-testid={`order-tracking-${o.order_id}`}>
                  {(o.carrier || "").toUpperCase()} {o.tracking_number}
                  {o.tracking_url && (
                    <> · <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-venom hover:underline">Track</a></>
                  )}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {o.status !== "shipped" && o.status !== "delivered" && o.status !== "canceled" && (
                  <button onClick={() => { setShipFor(shipFor === o.order_id ? null : o.order_id); setShip(SHIP_EMPTY); }} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] border border-venom/50 text-venom rounded-full px-4 py-2 hover:bg-[#A855F7] hover:text-white transition-all" data-testid={`order-ship-${o.order_id}`}>
                    <Truck size={13} /> Mark Shipped
                  </button>
                )}
                {o.status === "shipped" && (
                  <button onClick={() => patch(o.order_id, { status: "delivered" }, "Marked delivered — customer notified.")} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] border border-crystal/50 text-[#B983FF] rounded-full px-4 py-2 hover:bg-crystal hover:text-obsidian transition-all" data-testid={`order-deliver-${o.order_id}`}>
                    <CheckCheck size={13} /> Mark Delivered
                  </button>
                )}
                <DeleteBtn onClick={() => remove(o.order_id, o.customer_name)} label={`Delete order for ${o.customer_name}`} testId={`order-delete-${o.order_id}`} />
              </div>
              {shipFor === o.order_id && (
                <div className="mt-4 grid sm:grid-cols-3 gap-3 border-t border-white/10 pt-4" data-testid={`order-ship-form-${o.order_id}`}>
                  <select value={ship.carrier} onChange={(e) => setShip((s) => ({ ...s, carrier: e.target.value }))} className={inputCls} data-testid={`order-ship-carrier-${o.order_id}`}>
                    <option value="usps">USPS</option>
                    <option value="ups">UPS</option>
                    <option value="fedex">FedEx</option>
                    <option value="dhl">DHL</option>
                    <option value="other">Other</option>
                  </select>
                  <input value={ship.tracking_number} onChange={(e) => setShip((s) => ({ ...s, tracking_number: e.target.value }))} placeholder="Tracking number" className={inputCls} data-testid={`order-ship-number-${o.order_id}`} />
                  <input value={ship.tracking_url} onChange={(e) => setShip((s) => ({ ...s, tracking_url: e.target.value }))} placeholder="Tracking URL (auto if blank)" className={inputCls} data-testid={`order-ship-url-${o.order_id}`} />
                  <button onClick={() => patch(o.order_id, { status: "shipped", ...ship }, "Marked shipped — tracking email sent.")} className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-[#A855F7] text-white font-bold text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 transition-all" data-testid={`order-ship-submit-${o.order_id}`}>
                    <Truck size={14} /> Ship It &amp; Email Tracking
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const WRITER_KINDS = [
  { id: "press", label: "Press Release" },
  { id: "beat", label: "Beat Description" },
  { id: "email", label: "Email Reply" },
  { id: "social", label: "Social / Newsletter" },
];

function WriterTab() {
  const [kind, setKind] = useState("press");
  const [context, setContext] = useState("");
  const [result, setResult] = useState("");
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");

  const generate = async () => {
    if (!context.trim() || working) return;
    setMessage("");
    setResult("");
    setWorking(true);
    try {
      await streamSSE(`${API}/ai/write`, { kind, context: context.trim() }, (d) => setResult((r) => r + d));
    } catch (err) {
      setMessage(err.message);
    } finally {
      setWorking(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result);
      setMessage("Copied to clipboard.");
    } catch {
      setMessage("Select the text and copy it manually.");
    }
  };

  return (
    <div data-testid="writer-tab">
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">
        The label's AI staff writer (Gemini). Give it the facts — it drafts in the DGE voice. You review, edit, and publish.
      </p>
      <div className="bg-charcoal border border-crystal/25 rounded-2xl p-6">
        <div className="flex flex-wrap gap-2" data-testid="writer-kinds">
          {WRITER_KINDS.map((k) => (
            <button
              key={k.id}
              onClick={() => setKind(k.id)}
              className={`font-mono text-[11px] uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-all ${
                kind === k.id ? "bg-[#A855F7] text-white border-[#A855F7] font-semibold shadow-[0_0_16px_rgba(168,85,247,0.4)]" : "border-white/15 text-ice/60 hover:border-crystal/60 hover:text-ice"
              }`}
              data-testid={`writer-kind-${k.id}`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <textarea
          rows={4}
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder="Facts & instructions, e.g. “Announce the Signature Tee launch — 8 colorways, unisex, available at the33rd.store, est. 2026 branding.”"
          className={`${inputCls} resize-none mt-4`}
          data-testid="writer-context"
        />
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={generate}
            disabled={working || !context.trim()}
            className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all"
            data-testid="writer-generate"
          >
            {working ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {working ? "Writing…" : "Generate"}
          </button>
          {result && (
            <button onClick={copy} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] border border-white/15 rounded-full px-4 py-2 text-ice/70 hover:border-cyan-400/60 hover:text-cyan-300 transition-all" data-testid="writer-copy">
              <Copy size={13} /> Copy
            </button>
          )}
        </div>
        {message && <p className="mt-3 text-sm text-ice/70" data-testid="writer-message">{message}</p>}
        {result && (
          <div className="mt-5 bg-obsidian/70 border border-white/10 rounded-xl p-5" data-testid="writer-result">
            <p className="text-sm text-ice/85 leading-relaxed whitespace-pre-wrap">{result}</p>
          </div>
        )}
      </div>
    </div>
  );
}

const STAFF_EMPTY = { name: "", email: "", password: "", role: "aar" };

function TeamTab() {
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState(STAFF_EMPTY);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const res = await jsonApi("/auth/staff");
    if (res.ok) setStaff((await res.json()).staff || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setMessage("");
    setSaving(true);
    try {
      const res = await jsonApi("/auth/staff", { method: "POST", body: JSON.stringify(form) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Create failed.");
      setMessage(`Account created for ${form.email} — share the password with them directly (it is never shown again).`);
      setForm(STAFF_EMPTY);
      load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (u) => {
    const res = await jsonApi(`/auth/staff/${u.user_id}`, { method: "PATCH", body: JSON.stringify({ active: !u.active }) });
    if (res.ok) load();
  };

  return (
    <div data-testid="team-tab">
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">
        Staff access to the Label Ops Hub. Each role sees only the tools their job needs — Executives get full command,
        Managers run day-to-day ops, Artists see sessions &amp; upload music, Engineers get the studio floor, A&amp;R runs the talent pipeline.
        Executive accounts must complete MFA (authenticator app or email code) at sign-in.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="role-legend">
        {Object.entries(ROLE_META).map(([key, meta]) => (
          <div key={key} className="bg-[#0E0E12]/80 border border-[#282836] rounded-xl p-3.5" data-testid={`role-legend-${key}`}>
            <span className={`inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.12em] rounded-full px-2.5 py-1 border ${meta.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
            <p className="mt-2 text-[11px] text-ice/50 leading-relaxed">{meta.desc}</p>
          </div>
        ))}
      </div>

      <form onSubmit={create} className="bg-[#0E0E12]/90 border border-purple-500/25 rounded-2xl p-6 shadow-[0_0_40px_rgba(168,85,247,0.08)]" data-testid="staff-form">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">Provision a Staff Account</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <input required value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} data-testid="staff-input-name" />
          <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} data-testid="staff-input-email" />
          <input required type="password" minLength={8} value={form.password} onChange={set("password")} placeholder="Temporary password (min 8 chars) *" className={inputCls} data-testid="staff-input-password" />
          <select value={form.role} onChange={set("role")} className={inputCls} data-testid="staff-input-role">
            <option value="aar">A&amp;R / Talent Scout</option>
            <option value="engineer">Audio Engineer / Producer</option>
            <option value="manager">Manager (day-to-day ops)</option>
            <option value="artist">Artist (sessions + uploads)</option>
            <option value="executive">Admin / Executive (full access + MFA)</option>
          </select>
        </div>
        <div className="mt-3 flex items-center gap-2.5" data-testid="staff-role-preview">
          <span className={`inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.12em] rounded-full px-2.5 py-1 border ${ROLE_META[form.role]?.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${ROLE_META[form.role]?.dot}`} />
            {ROLE_META[form.role]?.label}
          </span>
          <span className="text-[11px] text-ice/45">{ROLE_META[form.role]?.desc}</span>
        </div>
        {message && <p className="mt-3 text-sm text-ice/70" data-testid="staff-message">{message}</p>}
        <div className="mt-5">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all" data-testid="staff-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <UserCog size={16} />}
            {saving ? "Creating…" : "Create Account"}
          </button>
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {staff === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
        ) : (
          staff.map((u) => (
            <div key={u.user_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-[#0E0E12]/80 border border-[#282836] hover:border-purple-500/40 rounded-xl px-5 py-4 transition-all duration-200" data-testid={`staff-card-${u.user_id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-display text-white text-sm font-bold">{u.name}</p>
                <p className="text-[11px] text-ice/50 truncate">{u.email}</p>
              </div>
              <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1 border ${ROLE_META[u.role]?.badge || "bg-white/8 text-ice/60 border-white/20"}`} data-testid={`staff-role-${u.user_id}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${ROLE_META[u.role]?.dot || "bg-white"}`} />
                {ROLE_LABELS[u.role] || u.role}
              </span>
              {u.role === "executive" && (
                <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-cyan-300/80" data-testid={`staff-mfa-${u.user_id}`}>
                  MFA {u.mfa?.totp_enabled ? "· TOTP" : ""}{u.mfa?.email_otp_enabled ? "· Email" : ""}{!u.mfa?.totp_enabled && !u.mfa?.email_otp_enabled ? "pending" : ""}
                </span>
              )}
              <button
                onClick={() => toggle(u)}
                className={`text-[11px] font-mono uppercase tracking-[0.12em] rounded-full px-3.5 py-1.5 border transition-all ${
                  u.active !== false ? "border-white/15 text-ice/60 hover:border-red-400/60 hover:text-red-400" : "border-venom/50 text-venom hover:bg-[#A855F7] hover:text-white"
                }`}
                data-testid={`staff-toggle-${u.user_id}`}
              >
                {u.active !== false ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const CONTENT_FIELDS = [
  { key: "hero_tagline", label: "Hero tagline (under the big title)" },
  { key: "hero_slogan", label: "Hero slogan (neon line)" },
  { key: "home_about_excerpt", label: "Homepage About excerpt" },
  { key: "newsletter_heading", label: "VIP mailing list heading" },
  { key: "booking_intro", label: "Booking page intro text" },
  { key: "talent_intro", label: "Get Signed (talent) page intro" },
  { key: "careers_intro", label: "Careers page intro" },
  { key: "contact_email", label: "Bookings & quotes email (footer, contact cards, email footers)" },
  { key: "support_email", label: "Support email (orders & help)" },
  { key: "press_email", label: "Press inquiries email" },
  { key: "contact_phones", label: "Phone numbers — one per line" },
  { key: "mailing_address", label: "Mailing address" },
];

function ContentTab() {
  const [content, setContent] = useState(null);
  const [saved, setSaved] = useState("");

  const load = useCallback(async () => {
    const res = await jsonApi("/content");
    if (res.ok) setContent((await res.json()).content || {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    const res = await jsonApi("/content", { method: "PUT", body: JSON.stringify({ key, value: content[key] || "" }) });
    if (res.ok) {
      setSaved(key);
      setTimeout(() => setSaved(""), 2000);
    }
  };

  if (content === null) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-cyan-400" size={26} /></div>;
  }

  return (
    <div data-testid="content-tab">
      <p className="text-sm text-ice/55 mb-6 leading-relaxed">
        Edit the main text blocks and the public contact details (emails, phones, address) shown across the site and in email footers.
        Changes go live on the website immediately after saving. Layout and design changes still come through your developer chat.
      </p>
      <div className="space-y-5">
        {CONTENT_FIELDS.map((f) => (
          <div key={f.key} className="bg-charcoal border border-crystal/20 rounded-xl p-5" data-testid={`content-field-${f.key}`}>
            <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">{f.label}</label>
            <textarea
              rows={f.key.includes("slogan") || f.key.includes("tagline") ? 2 : 4}
              value={content[f.key] || ""}
              onChange={(e) => setContent((c) => ({ ...c, [f.key]: e.target.value }))}
              className={`${inputCls} resize-none`}
              data-testid={`content-input-${f.key}`}
            />
            <button
              onClick={() => save(f.key)}
              className="mt-3 inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 transition-all duration-300"
              data-testid={`content-save-${f.key}`}
            >
              {saved === f.key ? "Saved ✓" : "Save"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscribersTab() {
  const [subs, setSubs] = useState(null);

  const load = useCallback(async () => {
    const res = await jsonApi("/newsletter");
    if (res.ok) setSubs((await res.json()).subscribers || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="subscribers-tab">
      <RefreshBtn onClick={load} testId="subscribers-refresh-button" />
      <div className="mt-5 space-y-2" data-testid="subscribers-list">
        {subs === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
        ) : subs.length === 0 ? (
          <p className="text-sm text-ice/45 text-center py-10" data-testid="subscribers-empty">No VIP subscribers yet.</p>
        ) : (
          subs.map((s) => (
            <div key={s.email} className="flex items-center justify-between bg-charcoal border border-crystal/20 rounded-xl px-5 py-3.5" data-testid={`subscriber-${s.email}`}>
              <span className="text-sm text-ice/80">{s.email}</span>
              <span className="font-mono text-[10px] text-ice/40">{fmtDate(s.created_at)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const relTime = (iso) => {
  try {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
};

function ActivityTab() {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    const res = await jsonApi("/activity");
    if (res.ok) setItems((await res.json()).activity || []);
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="activity-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-ice/55 max-w-xl leading-relaxed">
          Every staff action in the hub, as it happens. Refreshes automatically every 15 seconds.
        </p>
        <RefreshBtn onClick={load} testId="activity-refresh" />
      </div>
      {items === null ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-ice/45 text-center py-10" data-testid="activity-empty">No staff activity yet — actions will appear here the moment they happen.</p>
      ) : (
        <div className="space-y-2" data-testid="activity-feed">
          {items.map((a) => (
            <div key={a.a_id} className="flex items-center gap-3 bg-[#0E0E12]/80 border border-[#282836] rounded-xl px-4 py-3" data-testid={`activity-${a.a_id}`}>
              <span className={`shrink-0 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.1em] rounded-full px-2.5 py-1 border ${ROLE_META[a.actor_role]?.badge || "bg-white/8 text-ice/60 border-white/20"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${ROLE_META[a.actor_role]?.dot || "bg-white"}`} />
                {ROLE_META[a.actor_role]?.label || a.actor_role}
              </span>
              <p className="text-sm text-ice/80 min-w-0 truncate">
                <span className="text-white font-semibold">{a.actor_name || a.actor_email}</span> {a.summary}
              </p>
              <span className="ml-auto shrink-0 font-mono text-[10px] text-ice/40" title={fmtDate(a.created_at)}>{relTime(a.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const HR_PERSON_EMPTY = { name: "", role_title: "", department: "", email: "", phone: "", start_date: "", status: "onboarding", notes: "" };
const HR_STATUS_STYLE = {
  onboarding: "bg-amber-950/80 text-amber-300 border-amber-500/50",
  active: "bg-emerald-950/80 text-emerald-300 border-emerald-500/50",
  alumni: "bg-white/8 text-ice/50 border-white/20",
};

function HRTab() {
  const [view, setView] = useState("people");
  const [people, setPeople] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [form, setForm] = useState(HR_PERSON_EMPTY);
  const [taskForm, setTaskForm] = useState({ title: "", assignee: "", due: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [openId, setOpenId] = useState(null);
  const [newItem, setNewItem] = useState("");
  const [review, setReview] = useState({ rating: 5, note: "", period: "" });

  const load = useCallback(async () => {
    const [pr, tr] = await Promise.all([jsonApi("/hr/people"), jsonApi("/hr/tasks")]);
    if (pr.ok) setPeople((await pr.json()).people || []);
    if (tr.ok) setTasks((await tr.json()).tasks || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const tset = (k) => (e) => setTaskForm((f) => ({ ...f, [k]: e.target.value }));

  const addPerson = async (e) => {
    e.preventDefault();
    setBusy(true); setNote("");
    const res = await jsonApi("/hr/people", { method: "POST", body: JSON.stringify(form) });
    if (res.ok) { setForm(HR_PERSON_EMPTY); setNote("Team member added with a default onboarding checklist."); load(); }
    else setNote("Could not add person.");
    setBusy(false);
  };
  const removePerson = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the directory? Their docs and reviews go too.`)) return;
    const res = await api(`/hr/people/${p.p_id}`, { method: "DELETE" });
    if (res.ok) { setOpenId(null); load(); }
  };
  const toggleCheck = async (p, i, done) => {
    const res = await jsonApi(`/hr/people/${p.p_id}/checklist`, { method: "PATCH", body: JSON.stringify({ index: i, done }) });
    if (res.ok) load();
  };
  const addCheckItem = async (p) => {
    if (!newItem.trim()) return;
    const res = await jsonApi(`/hr/people/${p.p_id}/checklist`, { method: "POST", body: JSON.stringify({ label: newItem.trim() }) });
    if (res.ok) { setNewItem(""); load(); }
  };
  const uploadDoc = async (p, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("doc", file);
    fd.append("label", file.name);
    const res = await api(`/hr/people/${p.p_id}/docs`, { method: "POST", body: fd });
    if (res.ok) load(); else setNote("Doc upload failed (PDF/DOC/TXT/image, max 15MB).");
  };
  const deleteDoc = async (p, d) => {
    const res = await api(`/hr/people/${p.p_id}/docs/${d.doc_id}`, { method: "DELETE" });
    if (res.ok) load();
  };
  const addReview = async (p) => {
    if (!review.note.trim()) return;
    const res = await jsonApi(`/hr/people/${p.p_id}/reviews`, { method: "POST", body: JSON.stringify(review) });
    if (res.ok) { setReview({ rating: 5, note: "", period: "" }); load(); }
  };
  const addTask = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await jsonApi("/hr/tasks", { method: "POST", body: JSON.stringify(taskForm) });
    if (res.ok) { setTaskForm({ title: "", assignee: "", due: "", notes: "" }); load(); }
    setBusy(false);
  };
  const setTaskStatus = async (t, status) => {
    const res = await jsonApi(`/hr/tasks/${t.task_id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    if (res.ok) load();
  };
  const removeTask = async (t) => {
    const res = await api(`/hr/tasks/${t.task_id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  const inputD = inputCls;

  return (
    <div data-testid="hr-tab">
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">
        The label's people ops: directory &amp; profiles, onboarding checklists, contract/document storage, task assignment, and performance reviews.
      </p>
      <div className="flex gap-2 mb-6" data-testid="hr-subnav">
        {[["people", "People Directory"], ["tasks", "Tasks"]].map(([v, label]) => (
          <button key={v} onClick={() => setView(v)} className={`font-mono text-[11px] uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-all ${view === v ? "bg-[#A855F7] text-white border-[#A855F7] font-semibold shadow-[0_0_16px_rgba(168,85,247,0.4)]" : "border-[#282836] text-ice/60 hover:border-cyan-400/60 hover:text-cyan-300"}`} data-testid={`hr-view-${v}`}>
            {label}
          </button>
        ))}
      </div>
      {note && <p className="mb-4 text-sm text-ice/70" data-testid="hr-message">{note}</p>}

      {view === "people" && (
        <div>
          <form onSubmit={addPerson} className="bg-[#0E0E12]/90 border border-purple-500/25 rounded-2xl p-6 mb-6" data-testid="hr-person-form">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">Add Team Member</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <input required value={form.name} onChange={set("name")} placeholder="Full name *" className={inputD} data-testid="hr-input-name" />
              <input value={form.role_title} onChange={set("role_title")} placeholder="Role title (e.g. Mix Engineer)" className={inputD} data-testid="hr-input-role" />
              <input value={form.department} onChange={set("department")} placeholder="Department (Studio / A&R / Ops)" className={inputD} data-testid="hr-input-dept" />
              <input type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputD} data-testid="hr-input-email" />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputD} data-testid="hr-input-phone" />
              <input type="date" value={form.start_date} onChange={set("start_date")} className={`${inputD} [color-scheme:dark]`} data-testid="hr-input-start" />
            </div>
            <div className="mt-4">
              <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all" data-testid="hr-person-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Add to Directory
              </button>
            </div>
          </form>

          {people === null ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
          ) : people.length === 0 ? (
            <p className="text-sm text-ice/45 text-center py-10" data-testid="hr-people-empty">No team members yet — add your first above.</p>
          ) : (
            <div className="space-y-3" data-testid="hr-people-list">
              {people.map((p) => {
                const done = (p.checklist || []).filter((c) => c.done).length;
                const total = (p.checklist || []).length;
                const pct = total ? Math.round((done / total) * 100) : 0;
                const open = openId === p.p_id;
                return (
                  <div key={p.p_id} className="bg-[#0E0E12]/80 border border-[#282836] hover:border-purple-500/40 rounded-xl p-5 transition-all" data-testid={`hr-person-${p.p_id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      {p.photo ? (
                        <img src={mediaUrl(p.photo)} alt={p.name} className="w-11 h-11 rounded-full object-cover border border-purple-500/40" />
                      ) : (
                        <span className="w-11 h-11 rounded-full bg-purple-950/60 border border-purple-500/40 flex items-center justify-center font-display font-bold text-purple-300 text-sm">
                          {p.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-white text-sm font-bold">{p.name}</p>
                        <p className="text-[11px] text-ice/50">{p.role_title || "Team member"}{p.department ? ` · ${p.department}` : ""}{p.start_date ? ` · since ${p.start_date}` : ""}</p>
                      </div>
                      <span className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-3 py-1 border ${HR_STATUS_STYLE[p.status] || HR_STATUS_STYLE.onboarding}`} data-testid={`hr-status-${p.p_id}`}>{p.status}</span>
                      <div className="flex items-center gap-2" data-testid={`hr-onboard-${p.p_id}`}>
                        <div className="w-24 h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-[10px] text-ice/50">{pct}%</span>
                      </div>
                      <button onClick={() => setOpenId(open ? null : p.p_id)} className="text-[11px] font-mono uppercase tracking-[0.12em] border border-white/15 text-ice/60 rounded-full px-3.5 py-1.5 hover:border-cyan-400/60 hover:text-cyan-300 transition-all" data-testid={`hr-open-${p.p_id}`}>
                        {open ? "Close" : "Manage"}
                      </button>
                    </div>

                    {open && (
                      <div className="mt-5 grid lg:grid-cols-3 gap-5 border-t border-white/8 pt-5" data-testid={`hr-detail-${p.p_id}`}>
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mb-3">Onboarding Checklist</p>
                          <div className="space-y-1.5">
                            {(p.checklist || []).map((c, i) => (
                              <label key={i} className="flex items-center gap-2.5 text-xs text-ice/70 cursor-pointer group" data-testid={`hr-check-${p.p_id}-${i}`}>
                                <input type="checkbox" checked={c.done} onChange={(e) => toggleCheck(p, i, e.target.checked)} className="accent-purple-500 w-3.5 h-3.5" />
                                <span className={c.done ? "line-through text-ice/35" : "group-hover:text-white transition-colors"}>{c.label}</span>
                              </label>
                            ))}
                          </div>
                          <div className="mt-3 flex gap-2">
                            <input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Add checklist item" className={`${inputD} !py-2 text-xs`} data-testid={`hr-check-input-${p.p_id}`} />
                            <button onClick={() => addCheckItem(p)} className="shrink-0 text-xs font-bold bg-[#A855F7] text-white rounded-full px-4 hover:shadow-[0_0_16px_rgba(168,85,247,0.5)] transition-all" data-testid={`hr-check-add-${p.p_id}`}>Add</button>
                          </div>
                          <div className="mt-4 flex flex-wrap gap-2 items-center">
                            <label className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.12em] border border-white/15 text-ice/60 rounded-full px-3 py-1.5 hover:border-cyan-400/60 hover:text-cyan-300 cursor-pointer transition-all" data-testid={`hr-photo-${p.p_id}`}>
                              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                const f = e.target.files[0]; e.target.value = "";
                                if (!f) return;
                                const fd = new FormData(); fd.append("photo", f);
                                const res = await api(`/hr/people/${p.p_id}/photo`, { method: "POST", body: fd });
                                if (res.ok) load();
                              }} />
                              <ImagePlus size={12} /> Photo
                            </label>
                            <select value={p.status} onChange={async (e) => { await jsonApi(`/hr/people/${p.p_id}`, { method: "PATCH", body: JSON.stringify({ status: e.target.value }) }); load(); }} className="bg-[#14141A] border border-[#282836] rounded-full px-3 py-1.5 text-[11px] text-ice focus:outline-none focus:border-cyan-400/60" data-testid={`hr-status-select-${p.p_id}`}>
                              {["onboarding", "active", "alumni"].map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <DeleteBtn onClick={() => removePerson(p)} label={`Remove ${p.name}`} testId={`hr-delete-${p.p_id}`} />
                          </div>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mb-3">Documents &amp; Contracts</p>
                          <div className="space-y-1.5">
                            {(p.docs || []).map((d) => (
                              <div key={d.doc_id} className="flex items-center gap-2 text-xs" data-testid={`hr-doc-${d.doc_id}`}>
                                <FileText size={13} className="text-purple-300 shrink-0" />
                                <a href={`${BACKEND}/api/admin-files/${d.path}`} target="_blank" rel="noreferrer" className="text-ice/75 hover:text-cyan-300 truncate transition-colors">{d.label}</a>
                                <button onClick={() => deleteDoc(p, d)} className="ml-auto text-ice/35 hover:text-red-400 transition-colors" aria-label={`Delete ${d.label}`} data-testid={`hr-doc-del-${d.doc_id}`}><X size={13} /></button>
                              </div>
                            ))}
                            {!(p.docs || []).length && <p className="text-[11px] text-ice/35">No documents yet.</p>}
                          </div>
                          <label className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.12em] border border-dashed border-white/20 text-ice/60 rounded-full px-3.5 py-2 hover:border-cyan-400/60 hover:text-cyan-300 cursor-pointer transition-all" data-testid={`hr-doc-upload-${p.p_id}`}>
                            <input type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" className="hidden" onChange={(e) => { uploadDoc(p, e.target.files[0]); e.target.value = ""; }} />
                            <FileUp size={12} /> Upload Contract / Doc
                          </label>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mb-3">Performance Reviews</p>
                          <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                            {(p.reviews || []).map((r) => (
                              <div key={r.r_id} className="text-xs border-l-2 border-purple-500/40 pl-3 py-1" data-testid={`hr-review-${r.r_id}`}>
                                <div className="flex items-center gap-1 text-amber-300">
                                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={11} fill="currentColor" />)}
                                  <span className="ml-1.5 text-ice/40 font-mono text-[9px]">{r.period}</span>
                                </div>
                                <p className="text-ice/70 mt-1">{r.note}</p>
                                <p className="text-[9px] font-mono text-ice/35 mt-0.5">{r.by} · {fmtDate(r.created_at)}</p>
                              </div>
                            ))}
                            {!(p.reviews || []).length && <p className="text-[11px] text-ice/35">No reviews logged yet.</p>}
                          </div>
                          <div className="mt-3 space-y-2">
                            <div className="flex gap-2">
                              <select value={review.rating} onChange={(e) => setReview((r) => ({ ...r, rating: Number(e.target.value) }))} className="bg-[#14141A] border border-[#282836] rounded-xl px-2.5 py-2 text-xs text-ice" data-testid={`hr-review-rating-${p.p_id}`}>
                                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}★</option>)}
                              </select>
                              <input value={review.period} onChange={(e) => setReview((r) => ({ ...r, period: e.target.value }))} placeholder="Period (Q3 2026)" className={`${inputD} !py-2 text-xs`} data-testid={`hr-review-period-${p.p_id}`} />
                            </div>
                            <textarea rows={2} value={review.note} onChange={(e) => setReview((r) => ({ ...r, note: e.target.value }))} placeholder="Review note — wins, growth areas…" className={`${inputD} !py-2 text-xs resize-none`} data-testid={`hr-review-note-${p.p_id}`} />
                            <button onClick={() => addReview(p)} className="text-xs font-bold bg-[#A855F7] text-white rounded-full px-4 py-2 hover:shadow-[0_0_16px_rgba(168,85,247,0.5)] transition-all" data-testid={`hr-review-add-${p.p_id}`}>Log Review</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === "tasks" && (
        <div>
          <form onSubmit={addTask} className="bg-[#0E0E12]/90 border border-purple-500/25 rounded-2xl p-6 mb-6" data-testid="hr-task-form">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">Assign a Task</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <input required value={taskForm.title} onChange={tset("title")} placeholder="Task title *" className={inputD} data-testid="hr-task-title" />
              <select value={taskForm.assignee} onChange={tset("assignee")} className={inputD} data-testid="hr-task-assignee">
                <option value="">Assign to…</option>
                {(people || []).map((p) => <option key={p.p_id} value={p.name}>{p.name}</option>)}
              </select>
              <input type="date" value={taskForm.due} onChange={tset("due")} className={`${inputD} [color-scheme:dark]`} data-testid="hr-task-due" />
            </div>
            <div className="mt-4">
              <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all" data-testid="hr-task-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ClipboardList size={14} />} Assign Task
              </button>
            </div>
          </form>
          {tasks === null ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
          ) : tasks.length === 0 ? (
            <p className="text-sm text-ice/45 text-center py-10" data-testid="hr-tasks-empty">No tasks assigned yet.</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-4" data-testid="hr-tasks-board">
              {[["todo", "To Do"], ["doing", "In Progress"], ["done", "Done"]].map(([st, label]) => (
                <div key={st} className="bg-[#0E0E12]/60 border border-[#282836] rounded-xl p-4" data-testid={`hr-tasks-col-${st}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ice/50 mb-3">{label} · {tasks.filter((t) => t.status === st).length}</p>
                  <div className="space-y-2.5">
                    {tasks.filter((t) => t.status === st).map((t) => (
                      <div key={t.task_id} className="bg-[#14141A] border border-[#282836] rounded-lg p-3.5" data-testid={`hr-task-${t.task_id}`}>
                        <p className="text-sm text-white font-semibold">{t.title}</p>
                        <p className="text-[11px] text-ice/50 mt-1">{t.assignee || "Unassigned"}{t.due ? ` · due ${t.due}` : ""}</p>
                        <div className="mt-2.5 flex items-center gap-1.5">
                          {st !== "todo" && <button onClick={() => setTaskStatus(t, st === "doing" ? "todo" : "doing")} className="text-[10px] font-mono uppercase border border-white/15 text-ice/55 rounded-full px-2.5 py-1 hover:border-cyan-400/60 hover:text-cyan-300 transition-all" data-testid={`hr-task-back-${t.task_id}`}>← {st === "doing" ? "To Do" : "Doing"}</button>}
                          {st !== "done" && <button onClick={() => setTaskStatus(t, st === "todo" ? "doing" : "done")} className="text-[10px] font-mono uppercase border border-[#A855F7]/50 text-purple-300 rounded-full px-2.5 py-1 hover:bg-[#A855F7] hover:text-white transition-all" data-testid={`hr-task-fwd-${t.task_id}`}>{st === "todo" ? "Start" : "Done"} →</button>}
                          <button onClick={() => removeTask(t)} className="ml-auto text-ice/35 hover:text-red-400 transition-colors" aria-label="Delete task" data-testid={`hr-task-del-${t.task_id}`}><X size={13} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const VAULT_EMPTY = { title: "", kind: "link", url: "", body: "" };

function KbTab() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // article object or {} for new
  const [note, setNote] = useState("");
  const load = useCallback(async () => {
    const res = await jsonApi("/kb");
    if (res.ok) setArticles((await res.json()).articles || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (a) => {
    const isNew = !a._isExisting;
    const payload = { ...a, tags: typeof a.tags === "string" ? a.tags.split(",").map((t) => t.trim()).filter(Boolean) : a.tags };
    delete payload._isExisting;
    const res = isNew
      ? await jsonApi("/kb", { method: "POST", body: JSON.stringify(payload) })
      : await jsonApi(`/kb/${a.slug}`, { method: "PATCH", body: JSON.stringify(payload) });
    if (res.ok) { setNote(isNew ? "Article published." : "Article updated."); setEditing(null); load(); }
    else {
      const d = await res.json().catch(() => ({}));
      setNote(typeof d.detail === "string" ? d.detail : "Save failed.");
    }
  };
  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    const res = await api(`/kb/${slug}`, { method: "DELETE" });
    if (res.ok) load();
  };

  return (
    <div data-testid="knowledge-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-ice/55 max-w-xl leading-relaxed">
          The Knowledge Base powers the public Help Center (/help) and the internal staff manuals. Public articles are visitor-visible; Staff articles only show inside Label Ops.
        </p>
        <button onClick={() => setEditing({ slug: "", title: "", category: "Basics", audience: "public", tags: "", body: "" })}
          className="inline-flex items-center gap-2 border border-cyan-400/50 text-cyan-300 font-mono text-[11px] uppercase tracking-[0.12em] rounded-full px-4 py-2 hover:bg-cyan-500/10 transition-all"
          data-testid="kb-new">
          <BookOpen size={13} /> New Article
        </button>
      </div>
      {note && <p className="mb-4 text-sm text-ice/70" data-testid="kb-note">{note}</p>}

      {editing && (
        <div className="mb-6 rounded-2xl border border-cyan-500/30 bg-[#0E0E12] p-5 space-y-3" data-testid="kb-editor">
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={editing.slug} disabled={!!editing._isExisting} onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="slug-url-name"
              className="bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-cyan-400/70 disabled:opacity-50" data-testid="kb-slug" />
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Article title"
              className="bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-cyan-400/70" data-testid="kb-title" />
            <input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="Category (Basics, Troubleshooting…)"
              className="bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-cyan-400/70" data-testid="kb-category" />
            <select value={editing.audience} onChange={(e) => setEditing({ ...editing, audience: e.target.value })}
              className="bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-cyan-400/70" data-testid="kb-audience">
              <option value="public">Public (Help Center)</option>
              <option value="staff">Staff only (internal)</option>
            </select>
          </div>
          <input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="Search tags, comma separated"
            className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-cyan-400/70" data-testid="kb-tags" />
          <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={6} placeholder="Article body…"
            className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-cyan-400/70" data-testid="kb-body" />
          <div className="flex gap-2.5">
            <button onClick={() => save(editing)} className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-black font-bold text-xs px-6 py-2.5 rounded-full active:scale-95 transition-all" data-testid="kb-save">
              <Check size={13} /> Save Article
            </button>
            <button onClick={() => setEditing(null)} className="border border-white/15 text-ice/60 font-bold text-xs px-5 py-2.5 rounded-full hover:border-white/40 transition-all" data-testid="kb-cancel">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="kb-list">
        {articles.map((a) => (
          <div key={a.slug} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#121218] px-4 py-3.5" data-testid={`kb-row-${a.slug}`}>
            <span className={`font-mono text-[9px] uppercase tracking-[0.12em] border rounded-full px-2.5 py-1 shrink-0 ${a.audience === "staff" ? "border-purple-500/50 text-purple-300" : "border-emerald-500/50 text-emerald-300"}`}>{a.audience}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white font-semibold truncate">{a.title}</p>
              <p className="text-[11px] text-ice/45">{a.category} · /{a.slug}</p>
            </div>
            <button onClick={() => setEditing({ ...a, tags: (a.tags || []).join(", "), _isExisting: true })} className="text-ice/40 hover:text-cyan-300 transition-colors" aria-label={`Edit ${a.title}`} data-testid={`kb-edit-${a.slug}`}>
              <Pencil size={15} />
            </button>
            <button onClick={() => remove(a.slug)} className="text-ice/40 hover:text-red-400 transition-colors" aria-label={`Delete ${a.title}`} data-testid={`kb-delete-${a.slug}`}>
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArcadeTab() {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  const GAME_LABELS = { piano: "Piano Scales", quiz: "Venom Drop", spiral: "Serpent Spiral", bass: "Drop the Bass", memory: "Beat Builder" };
  const GAMES = ["all", "piano", "quiz", "spiral", "bass", "memory"];
  const load = useCallback(async () => {
    const res = await jsonApi("/arcade/admin");
    if (res.ok) setData(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeScore = async (s) => {
    const res = await api(`/arcade/scores/${s.s_id}`, { method: "DELETE" });
    if (res.ok) load();
  };
  const resetBoard = async () => {
    const label = gameFilter === "all" ? "ALL FIVE leaderboards" : `the ${GAME_LABELS[gameFilter]} leaderboard`;
    if (!window.confirm(`Reset ${label}? Prize records are kept.`)) return;
    const res = await jsonApi("/arcade/reset", { method: "POST", body: JSON.stringify(gameFilter === "all" ? {} : { game: gameFilter }) });
    if (res.ok) { setNote("Leaderboard wiped — fresh competition starts now."); load(); }
  };
  const setPrizeStatus = async (p, status) => {
    const res = await jsonApi(`/arcade/prizes/${p.prize_id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    if (res.ok) load();
  };

  const allScores = data?.scores || [];
  const scores = gameFilter === "all" ? allScores : allScores.filter((s) => (s.game || "piano") === gameFilter);
  const prizes = (data?.prizes || []).filter((p) => gameFilter === "all" || (p.game || "piano") === gameFilter);
  const PRIZE_BADGE = {
    won: "text-yellow-300 border-yellow-500/50",
    claimed: "text-cyan-300 border-cyan-500/50",
    sent: "text-emerald-300 border-emerald-500/50",
  };

  return (
    <div data-testid="arcade-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-ice/55 max-w-xl leading-relaxed">
          Five-game arcade moderation — Piano Scales, Venom Drop, Serpent Spiral, Drop the Bass, Beat Builder. Winners get unique one-time codes auto-generated at the moment they win.
        </p>
        <button onClick={resetBoard} className="inline-flex items-center gap-2 border border-red-500/50 text-red-300 font-mono text-[11px] uppercase tracking-[0.12em] rounded-full px-4 py-2 hover:bg-red-500 hover:text-white transition-all" data-testid="arcade-reset">
          <Trophy size={13} /> Reset Leaderboard
        </button>
      </div>
      {note && <p className="mb-4 text-sm text-ice/70" data-testid="arcade-note">{note}</p>}

      <div className="flex flex-wrap gap-2 mb-6" data-testid="arcade-game-filter">
        {GAMES.map((g) => (
          <button key={g} onClick={() => setGameFilter(g)}
            className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-4 py-2 border transition-all ${gameFilter === g ? "border-cyan-400 text-cyan-300 bg-cyan-500/10" : "border-white/15 text-ice/50 hover:border-cyan-400/50 hover:text-cyan-300"}`}
            data-testid={`arcade-filter-${g}`}>
            {g === "all" ? "All Games" : GAME_LABELS[g]}
          </button>
        ))}
      </div>

      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-3">Leaderboard ({scores.length})</p>
      <div className="space-y-2 mb-8" data-testid="arcade-scores">
        {data === null ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-cyan-400" size={22} /></div>
        ) : scores.length === 0 ? (
          <p className="text-sm text-ice/45 py-6 text-center" data-testid="arcade-scores-empty">No scores yet — the board fills as players post runs.</p>
        ) : (
          scores.map((s, i) => (
            <div key={s.s_id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 bg-[#0E0E12]/80 border border-[#282836] rounded-xl px-4 py-3" data-testid={`arcade-score-${s.s_id}`}>
              <span className={`font-mono text-xs w-8 ${i === 0 ? "text-yellow-300" : "text-ice/40"}`}>#{i + 1}</span>
              <span className="text-sm text-white font-semibold">{s.name}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-fuchsia-300 border border-fuchsia-500/40 rounded-full px-2 py-0.5" data-testid={`arcade-game-${s.s_id}`}>{GAME_LABELS[s.game || "piano"]}</span>
              <span className="font-mono text-sm text-ice/85">{s.score.toLocaleString()}</span>
              <span className="font-mono text-[10px] text-ice/45">combo ×{s.best_combo}</span>
              {s.prize && <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-yellow-300 border border-yellow-500/40 rounded-full px-2 py-0.5">{s.prize}</span>}
              {s.prize_code && <span className="font-mono text-[10px] text-emerald-300 tracking-widest" data-testid={`arcade-code-${s.s_id}`}>{s.prize_code}</span>}
              <span className="font-mono text-[10px] text-ice/40">{fmtDate(s.created_at)}</span>
              <span className="ml-auto"><DeleteBtn onClick={() => removeScore(s)} label={`Remove score by ${s.name}`} testId={`arcade-del-${s.s_id}`} /></span>
            </div>
          ))
        )}
      </div>

      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-3">Prize Fulfillment ({prizes.length})</p>
      <div className="space-y-2" data-testid="arcade-prizes">
        {prizes.length === 0 ? (
          <p className="text-sm text-ice/45 py-6 text-center" data-testid="arcade-prizes-empty">No winners yet — prize tiers vary per game (from 150 up to 10,000 points).</p>
        ) : (
          prizes.map((p) => (
            <div key={p.prize_id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 bg-[#0E0E12]/80 border border-[#282836] rounded-xl px-4 py-3" data-testid={`arcade-prize-${p.prize_id}`}>
              <div className="min-w-0">
                <p className="text-sm text-white font-semibold">{p.player_name} <span className="text-ice/45 font-normal text-xs">{p.email}</span></p>
                <p className="text-[11px] text-ice/50">{GAME_LABELS[p.game || "piano"]} · {p.prize} · {p.score.toLocaleString()} pts{p.code ? ` · code ${p.code}` : ""}{p.emailed ? " · emailed" : ""}</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                {["won", "claimed", "sent"].map((st) => (
                  <button key={st} onClick={() => setPrizeStatus(p, st)} disabled={p.status === st}
                    className={`font-mono text-[10px] uppercase tracking-[0.1em] rounded-full px-3 py-1.5 border transition-all disabled:opacity-100 ${p.status === st ? PRIZE_BADGE[st] + " bg-white/5" : "border-white/15 text-ice/50 hover:border-cyan-400/60 hover:text-cyan-300"}`}
                    data-testid={`prize-${st}-${p.prize_id}`}>
                    {st}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function DropsTab() {
  const [drops, setDrops] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [date, setDate] = useState("");
  const [trackId, setTrackId] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const [dr, tr] = await Promise.all([jsonApi("/drops"), fetch(`${API}/tracks`)]);
    if (dr.ok) setDrops((await dr.json()).drops || []);
    if (tr.ok) {
      const list = (await tr.json()).tracks || [];
      setTracks(list);
      if (list.length && !trackId) setTrackId(list[0].id);
    }
  }, [trackId]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const schedule = async (e) => {
    e.preventDefault();
    setNote("");
    const res = await jsonApi("/drops", { method: "POST", body: JSON.stringify({ date, track_id: trackId }) });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { setNote(`Locked: ${d.track} petrifies on ${d.date} (expires at midnight ET).`); load(); }
    else setNote(typeof d.detail === "string" ? d.detail : "Could not schedule.");
  };
  const remove = async (d) => {
    const res = await api(`/drops/${d.date}`, { method: "DELETE" });
    if (res.ok) load();
  };

  const todayET = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

  return (
    <div data-testid="drops-tab">
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">
        Program the Petrified Countdown. Each scheduled beat becomes the Medusa Room's Vault-Locked drop for that day (midnight-to-midnight, Eastern).
        Days you don't program are auto-filled by the rotation engine at midnight.
      </p>
      <form onSubmit={schedule} className="bg-[#0E0E12]/90 border border-purple-500/25 rounded-2xl p-6 mb-6" data-testid="drops-form">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">Schedule a Drop</p>
        <div className="grid sm:grid-cols-[180px_1fr_auto] gap-3">
          <input required type="date" value={date} min={todayET} onChange={(e) => setDate(e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="drops-date" />
          <select value={trackId} onChange={(e) => setTrackId(e.target.value)} className={inputCls} data-testid="drops-track">
            {tracks.map((t) => <option key={t.id} value={t.id}>{t.title}{t.bpm ? ` — ${t.bpm} BPM` : ""}</option>)}
          </select>
          <button type="submit" className="inline-flex items-center justify-center gap-2 bg-[#A855F7] text-white font-bold text-xs px-5 py-3 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 transition-all" data-testid="drops-submit">
            <CalendarClock size={14} /> Lock It In
          </button>
        </div>
        {note && <p className="mt-3 text-xs text-ice/60" data-testid="drops-note">{note}</p>}
      </form>
      <div className="space-y-2" data-testid="drops-list">
        {drops === null ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-cyan-400" size={22} /></div>
        ) : drops.length === 0 ? (
          <p className="text-sm text-ice/45 py-6 text-center" data-testid="drops-empty">Nothing scheduled — the auto-rotation engine will pick today's beat at midnight.</p>
        ) : (
          drops.map((d) => (
            <div key={d.drop_id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 bg-[#0E0E12]/80 border border-[#282836] rounded-xl px-4 py-3" data-testid={`drop-${d.date}`}>
              <span className="font-mono text-xs text-white">{d.date}</span>
              <span className="text-sm text-ice/85">{d.track_title}</span>
              {d.auto && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-cyan-300 border border-cyan-500/40 rounded-full px-2 py-0.5">auto</span>}
              {d.expired && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-zinc-400 border border-zinc-600/50 rounded-full px-2 py-0.5">petrified</span>}
              {d.date === todayET && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-yellow-300 border border-yellow-500/50 rounded-full px-2 py-0.5">live today</span>}
              <span className="ml-auto"><DeleteBtn onClick={() => remove(d)} label={`Remove drop ${d.date}`} testId={`drop-del-${d.date}`} /></span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function DemosTab() {
  const [items, setItems] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(async () => {
    const res = await jsonApi("/talent");
    if (res.ok) {
      const all = (await res.json()).items || [];
      setItems(all.filter((t) => (t.message || "").includes("Snake Pit")));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (t, status) => {
    const res = await jsonApi(`/talent/${t.t_id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    if (res.ok) { setNote(status === "callback" ? `${t.stage_name} moved to Callback.` : `${t.stage_name} passed.`); load(); }
  };

  return (
    <div data-testid="demos-tab">
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">
        Raw 30-second vocal ideas recorded live in the Medusa Room's Snake Pit. Listen, then push to Callback or pass. Full applications live in the Talent tab.
      </p>
      {note && <p className="mb-4 text-sm text-ice/70" data-testid="demos-note">{note}</p>}
      {items === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-white/12 rounded-2xl" data-testid="demos-empty">
          <FileAudio size={28} className="mx-auto text-ice/25 mb-3" />
          <p className="text-sm text-ice/45">The pit is quiet — Serpent Seed challenges keep ideas flowing in.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="demos-list">
          {items.map((t) => (
            <div key={t.t_id} className="bg-[#0E0E12]/80 border border-[#282836] hover:border-pink-500/40 rounded-xl p-5 transition-all" data-testid={`demo-${t.t_id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-0 flex-1">
                  <p className="font-display text-white text-sm font-bold">{t.stage_name}</p>
                  <p className="text-[11px] text-ice/50">{t.email} · {fmtDate(t.created_at)}</p>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-3 py-1 border ${STATUS_STYLE[t.status] || STATUS_STYLE.new}`}>{t.status.replace(/_/g, " ")}</span>
              </div>
              {t.demo_file && (
                <audio controls preload="none" src={`${BACKEND}${t.demo_file}`} className="mt-3 w-full h-9 rounded-lg" data-testid={`demo-audio-${t.t_id}`} />
              )}
              <div className="mt-3 flex gap-2">
                <button onClick={() => decide(t, "callback")} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-emerald-500/50 text-emerald-300 rounded-full px-4 py-2 hover:bg-emerald-500 hover:text-black transition-all" data-testid={`demo-accept-${t.t_id}`}>
                  <Check size={12} /> Callback
                </button>
                <button onClick={() => decide(t, "passed")} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-white/15 text-ice/55 rounded-full px-4 py-2 hover:border-red-400/60 hover:text-red-300 transition-all" data-testid={`demo-reject-${t.t_id}`}>
                  <X size={12} /> Pass
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MedusaTab() {
  const [data, setData] = useState(null);
  const [vault, setVault] = useState(null);
  const [form, setForm] = useState(VAULT_EMPTY);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const [mr, vr] = await Promise.all([jsonApi("/medusa/members"), jsonApi("/vault")]);
    if (mr.ok) setData(await mr.json());
    if (vr.ok) setVault((await vr.json()).items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addItem = async (e) => {
    e.preventDefault();
    setBusy(true); setNote("");
    const res = await jsonApi("/vault", { method: "POST", body: JSON.stringify(form) });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { setForm(VAULT_EMPTY); setNote("Vault drop added — live for members now."); load(); }
    else setNote(typeof d.detail === "string" ? d.detail : "Could not add item.");
    setBusy(false);
  };
  const removeItem = async (it) => {
    const res = await api(`/vault/${it.v_id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  const stats = data?.stats || {};
  return (
    <div data-testid="medusa-tab">
      <p className="text-sm text-ice/55 mb-5 leading-relaxed max-w-2xl">
        The Medusa Room VIP membership ($9.99/mo with a 7-day free trial, or $99/yr). Track members, revenue, and post members-only drops to the Vault.
      </p>
      <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-6" data-testid="medusa-stats">
        {[["Active Members", stats.active ?? 0], ["Est. MRR", `$${(stats.mrr ?? 0).toFixed(2)}`], ["Churned", stats.canceled ?? 0]].map(([label, val]) => (
          <div key={label} className="bg-charcoal border border-crystal/20 rounded-xl p-4 sm:p-6" data-testid={`medusa-stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <Crown size={18} className="text-purple-300 mb-2" />
            <p className="font-display text-2xl sm:text-3xl font-bold text-white">{val}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ice/50 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-3">Members</p>
      <div className="space-y-2 mb-8" data-testid="medusa-members">
        {data === null ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-cyan-400" size={22} /></div>
        ) : data.members.length === 0 ? (
          <p className="text-sm text-ice/45 py-6 text-center" data-testid="medusa-members-empty">No members yet — share the Medusa Room page to start signups.</p>
        ) : (
          data.members.map((m) => (
            <div key={m.member_id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 bg-[#0E0E12]/80 border border-[#282836] rounded-xl px-4 py-3" data-testid={`medusa-member-${m.member_id}`}>
              <span className="text-sm text-ice/85">{m.email}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ice/50">{m.plan}</span>
              <span className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-2.5 py-0.5 border ${["active", "trialing"].includes(m.status) ? "text-emerald-300 border-emerald-500/40" : m.status === "past_due" ? "text-amber-300 border-amber-500/40" : "text-red-300 border-red-500/40"}`}>{m.status}{m.cancel_at_period_end ? " · ends" : ""}</span>
              <span className="ml-auto font-mono text-[10px] text-ice/40">{m.current_period_end ? `renews ${new Date(m.current_period_end).toLocaleDateString()}` : ""}</span>
            </div>
          ))
        )}
      </div>

      <form onSubmit={addItem} className="bg-[#0E0E12]/90 border border-purple-500/25 rounded-2xl p-6" data-testid="vault-form">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">Post to the Vault (members-only)</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Drop title *" className={inputCls} data-testid="vault-input-title" />
          <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} className={inputCls} data-testid="vault-input-kind">
            <option value="link">Link (unreleased track, video, download)</option>
            <option value="note">Note / announcement</option>
          </select>
        </div>
        {form.kind === "link" ? (
          <input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://…" className={`${inputCls} mt-3`} data-testid="vault-input-url" />
        ) : (
          <textarea rows={3} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder="Members-only message…" className={`${inputCls} mt-3 resize-none`} data-testid="vault-input-body" />
        )}
        <div className="mt-4 flex items-center gap-3">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-[#A855F7] text-white font-bold text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-95 disabled:opacity-60 transition-all" data-testid="vault-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />} Publish Drop
          </button>
          {note && <p className="text-xs text-ice/60" data-testid="vault-note">{note}</p>}
        </div>
      </form>
      <div className="mt-5 space-y-2" data-testid="vault-list">
        {(vault || []).map((it) => (
          <div key={it.v_id} className="flex items-center gap-3 bg-[#0E0E12]/80 border border-[#282836] rounded-xl px-4 py-3" data-testid={`vault-item-${it.v_id}`}>
            <span className="font-mono text-[9px] uppercase tracking-[0.1em] rounded-full px-2 py-0.5 border border-cyan-400/40 text-cyan-300">{it.kind}</span>
            <span className="text-sm text-ice/85 truncate">{it.title}</span>
            <span className="ml-auto font-mono text-[10px] text-ice/40">{fmtDate(it.created_at)}</span>
            <DeleteBtn onClick={() => removeItem(it)} label={`Remove ${it.title}`} testId={`vault-del-${it.v_id}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("leads");
  // MFA stages
  const [mfa, setMfa] = useState(null); // { pre_token, mode: "verify"|"enroll", methods?, method, totp? }
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);

  useEffect(() => {
    jsonApi("/auth/me").then(async (res) => {
      setUser(res.ok ? await res.json() : false);
    });
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await jsonApi("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.mfa_required) {
          setMfa({ pre_token: data.pre_token, mode: "verify", methods: data.methods, method: data.methods[0], email: data.email });
        } else if (data.mfa_setup_required) {
          setMfa({ pre_token: data.pre_token, mode: "enroll", method: null, email: data.email });
        } else {
          setUser(data);
        }
      } else {
        setLoginError(typeof data.detail === "string" ? data.detail : "Login failed. Check your credentials.");
      }
    } catch {
      setLoginError("Network error. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const startEnroll = async (method) => {
    setLoginError("");
    setMfaBusy(true);
    try {
      const res = await jsonApi("/auth/mfa/enroll", { method: "POST", body: JSON.stringify({ pre_token: mfa.pre_token, method }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Setup failed.");
      setMfa((m) => ({ ...m, method, totp: method === "totp" ? data : null, enrollSent: method === "email" }));
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setMfaBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoginError("");
    setMfaBusy(true);
    const endpoint = mfa.mode === "verify" ? "/auth/mfa/verify" : "/auth/mfa/confirm";
    try {
      const res = await jsonApi(endpoint, {
        method: "POST",
        body: JSON.stringify({ pre_token: mfa.pre_token, method: mfa.method, code: mfaCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMfa(null);
        setMfaCode("");
        setUser(data);
      } else {
        setLoginError(typeof data.detail === "string" ? data.detail : "Invalid code.");
      }
    } catch {
      setLoginError("Network error. Please try again.");
    } finally {
      setMfaBusy(false);
    }
  };

  const resendEmailCode = async () => {
    await jsonApi("/auth/mfa/email-code", { method: "POST", body: JSON.stringify({ pre_token: mfa.pre_token, method: "email" }) });
  };

  const logout = async () => {
    await jsonApi("/auth/logout", { method: "POST" });
    setUser(false);
    setMfa(null);
  };

  const role = user && user.role ? user.role : "executive";
  const visibleTabs = TABS.filter((t) => !t.roles || t.roles.includes(role));
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : visibleTabs[0]?.id;

  return (
    <main className="min-h-screen bg-[#070709] text-ice relative overflow-x-hidden" data-testid="admin-page">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#A855F7]/12 blur-[140px]" />
        <div className="absolute top-1/3 -right-48 w-[520px] h-[520px] rounded-full bg-[#06B6D4]/10 blur-[140px]" />
        <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-[#8A2BE2]/8 blur-[120px]" />
      </div>
      <header className="relative z-10 bg-[#0A0A0C]/80 backdrop-blur-md border-b border-purple-900/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <LogoMark className="w-9 h-9" />
            <span className="font-display font-bold text-white text-sm">
              DGE<span className="text-cyan-400">™</span>
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-purple-300/60">Label Ops Hub</span>
            </span>
          </Link>
          {user && (
            <div className="flex items-center gap-4">
              <span className={`hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1 border ${ROLE_META[role]?.badge || ""}`} data-testid="admin-header-role-badge">
                <span className={`w-1.5 h-1.5 rounded-full ${ROLE_META[role]?.dot || "bg-white"}`} />
                {ROLE_LABELS[role] || role}
              </span>
              <button
                onClick={logout}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-ice/60 hover:text-red-400 transition-colors"
                data-testid="admin-logout-button"
              >
                <LogOut size={15} /> Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {user === null ? (
        <div className="relative z-10 flex items-center justify-center py-32">
          <Loader2 className="animate-spin text-cyan-400" size={28} />
        </div>
      ) : user === false && !mfa ? (
        <div className="relative z-10 flex items-center justify-center px-4 py-20">
          <form onSubmit={login} className="relative overflow-hidden w-full max-w-md bg-[#0E0E12]/90 border border-purple-500/30 rounded-2xl p-8 backdrop-blur-xl shadow-[0_0_50px_rgba(168,85,247,0.15)]" data-testid="admin-login-form">
            <div aria-hidden="true" className="absolute top-0 left-0 h-[2px] w-full bg-gradient-to-r from-purple-500 via-cyan-400 to-purple-600" />
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-2">Restricted · Label Personnel</p>
            <h1 className="font-display text-xl font-bold text-white">Label Ops Hub</h1>
            <p className="mt-2 text-xs text-ice/50">For DGE: Cold-Blooded Records LLC™ team only. Executives sign in with MFA.</p>
            <div className="mt-6 space-y-4">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" className={inputCls} data-testid="admin-login-email" />
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} data-testid="admin-login-password" />
            </div>
            {loginError && (
              <p className="mt-4 text-sm text-red-400" role="alert" data-testid="admin-login-error">{loginError}</p>
            )}
            <button type="submit" disabled={loggingIn} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-[0.98] disabled:opacity-60 transition-all duration-300" data-testid="admin-login-submit">
              {loggingIn && <Loader2 size={16} className="animate-spin" />}
              {loggingIn ? "Signing in…" : "Sign In"}
            </button>
            <p className="mt-5 text-center text-[11px] text-ice/40">
              Trouble signing in? The Label Ops Guide tab (inside the hub) covers MFA, roles, and fixes — or contact your executive.
            </p>
          </form>
        </div>
      ) : user === false && mfa ? (
        <div className="relative z-10 flex items-center justify-center px-4 py-20">
          <div className="relative overflow-hidden w-full max-w-md bg-[#0E0E12]/90 border border-purple-500/30 rounded-2xl p-8 backdrop-blur-xl shadow-[0_0_50px_rgba(168,85,247,0.15)]" data-testid="admin-mfa-panel">
            <div aria-hidden="true" className="absolute top-0 left-0 h-[2px] w-full bg-gradient-to-r from-purple-500 via-cyan-400 to-purple-600" />
            {mfa.mode === "verify" ? (
              <form onSubmit={submitMfa}>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-2">Two-Factor Check</p>
                <h1 className="font-display text-xl font-bold text-white">Verify it&rsquo;s you</h1>
                {mfa.methods.length > 1 && (
                  <div className="mt-5 flex gap-2" data-testid="admin-mfa-methods">
                    {mfa.methods.map((m) => (
                      <button key={m} type="button" onClick={() => setMfa((s) => ({ ...s, method: m }))} className={`flex-1 font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-3 py-2 border transition-all ${mfa.method === m ? "bg-[#A855F7] text-white border-[#A855F7] font-semibold shadow-[0_0_16px_rgba(168,85,247,0.4)]" : "border-white/15 text-ice/60"}`} data-testid={`admin-mfa-method-${m}`}>
                        {m === "totp" ? "Authenticator" : "Email Code"}
                      </button>
                    ))}
                  </div>
                )}
                <p className="mt-4 text-xs text-ice/55 leading-relaxed">
                  {mfa.method === "totp"
                    ? "Enter the 6-digit code from your authenticator app."
                    : <>We emailed a 6-digit code to <span className="text-ice/80">{mfa.email}</span>. <button type="button" onClick={resendEmailCode} className="text-cyan-300 hover:underline" data-testid="admin-mfa-resend">Resend</button></>}
                </p>
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={8} required value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="6-digit code" className={`${inputCls} mt-4 text-center text-lg tracking-[0.4em] font-mono`} data-testid="admin-mfa-code-input" />
                {loginError && <p className="mt-4 text-sm text-red-400" role="alert" data-testid="admin-mfa-error">{loginError}</p>}
                <button type="submit" disabled={mfaBusy} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-[0.98] disabled:opacity-60 transition-all" data-testid="admin-mfa-submit">
                  {mfaBusy && <Loader2 size={16} className="animate-spin" />}
                  Verify &amp; Sign In
                </button>
                <button type="button" onClick={() => { setMfa(null); setMfaCode(""); setLoginError(""); }} className="mt-4 w-full text-xs font-mono uppercase tracking-[0.15em] text-ice/45 hover:text-white transition-colors" data-testid="admin-mfa-back">
                  Back to login
                </button>
              </form>
            ) : (
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-2">Executive Security</p>
                <h1 className="font-display text-xl font-bold text-white">Set up MFA to continue</h1>
                <p className="mt-3 text-xs text-ice/55 leading-relaxed">
                  Executive accounts require multi-factor authentication. Choose a method — you only do this once per device change.
                </p>
                {!mfa.method ? (
                  <div className="mt-6 space-y-3" data-testid="admin-enroll-choices">
                    <button onClick={() => startEnroll("totp")} disabled={mfaBusy} className="w-full inline-flex items-center justify-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-[0.98] disabled:opacity-60 transition-all" data-testid="admin-enroll-totp">
                      {mfaBusy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                      Authenticator App (recommended)
                    </button>
                    <button onClick={() => startEnroll("email")} disabled={mfaBusy} className="w-full inline-flex items-center justify-center gap-2 border border-white/15 text-ice/70 font-semibold text-sm px-6 py-3.5 rounded-full hover:border-cyan-400/60 hover:text-cyan-300 transition-all" data-testid="admin-enroll-email">
                      Email Code to {mfa.email}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submitMfa} className="mt-5">
                    {mfa.method === "totp" && mfa.totp && (
                      <div className="text-center" data-testid="admin-enroll-qr">
                        <div className="inline-block bg-white p-3 rounded-xl">
                          <QRCodeSVG value={mfa.totp.otpauth_uri} size={168} />
                        </div>
                        <p className="mt-3 text-[11px] text-ice/50">Scan with Google Authenticator / Authy, or enter manually:</p>
                        <p className="mt-1.5 font-mono text-xs text-purple-300 tracking-[0.15em] break-all select-all" data-testid="admin-enroll-secret">{mfa.totp.secret}</p>
                      </div>
                    )}
                    {mfa.method === "email" && (
                      <p className="text-xs text-ice/55 leading-relaxed" data-testid="admin-enroll-email-sent">
                        Code sent to <span className="text-ice/80">{mfa.email}</span>. Check your inbox.
                      </p>
                    )}
                    <input inputMode="numeric" autoComplete="one-time-code" maxLength={8} required value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="Enter the 6-digit code" className={`${inputCls} mt-4 text-center text-lg tracking-[0.4em] font-mono`} data-testid="admin-mfa-code-input" />
                    {loginError && <p className="mt-4 text-sm text-red-400" role="alert" data-testid="admin-mfa-error">{loginError}</p>}
                    <button type="submit" disabled={mfaBusy} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#A855F7] text-white font-bold text-sm px-6 py-3.5 rounded-full hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] active:scale-[0.98] disabled:opacity-60 transition-all" data-testid="admin-mfa-submit">
                      {mfaBusy && <Loader2 size={16} className="animate-spin" />}
                      Confirm &amp; Sign In
                    </button>
                  </form>
                )}
                {loginError && !mfa.method && <p className="mt-4 text-sm text-red-400" role="alert" data-testid="admin-mfa-error">{loginError}</p>}
                <button type="button" onClick={() => { setMfa(null); setMfaCode(""); setLoginError(""); }} className="mt-4 w-full text-xs font-mono uppercase tracking-[0.15em] text-ice/45 hover:text-white transition-colors" data-testid="admin-mfa-back">
                  Back to login
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="leads-dashboard">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">Label Ops Hub</p>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl font-bold text-white flex flex-wrap items-center gap-3">
            Command Center
            <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] font-normal border rounded-full px-3 py-1 ${ROLE_META[role]?.badge || "text-crystal border-crystal/40"}`} data-testid="admin-role-badge">
              <span className={`w-1.5 h-1.5 rounded-full ${ROLE_META[role]?.dot || "bg-white"}`} />
              {ROLE_LABELS[role] || role}
            </span>
          </h1>
          <p className="mt-2 text-xs text-ice/45 max-w-xl" data-testid="admin-role-desc">{ROLE_META[role]?.desc}</p>

          <div className="mt-7 flex flex-wrap gap-2" data-testid="admin-tabs">
            {visibleTabs.map((t) => {
              const Icon = t.Icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] rounded-full px-4 py-2.5 border transition-all duration-300 ${
                    tab === t.id ? "bg-[#A855F7] text-white border-[#A855F7] font-semibold shadow-[0_0_16px_rgba(168,85,247,0.4)]" : "border-[#282836] bg-[#0E0E12]/70 text-ice/60 hover:border-cyan-400/60 hover:text-cyan-300"
                  }`}
                  data-testid={`tab-${t.id}`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>

          <div className="mt-8">
            {activeTab === "leads" && <LeadsTab />}
            {activeTab === "talent" && (
              <ApplicationsTab
                endpoint="talent"
                idField="t_id"
                nameField="stage_name"
                fields={[["Real Name", "real_name"], ["Genre", "genre"], ["Location", "location"], ["Phone", "phone"], ["Demo Link", "demo_link"], ["EPK / Social", "links"], ["Demo File", "demo_name"], ["18+", "age_confirmed"]]}
                statuses={["new", "under_review", "follow_up", "aar_approved", "passed"]}
                statusLabels={{ new: "New", under_review: "Under Review", follow_up: "Follow Up", aar_approved: "A&R Approved", passed: "Passed" }}
                emptyLabel="No talent submissions yet."
                testPrefix="talent"
                description="Artists asking to join the roster. Play demos right here, move them through the pipeline, send one-click A&R replies, and feature the best Snake Pit sends as public Ghost Verses."
                photoSupport
                ghostSupport
                replies={[
                  ["follow_up", "Follow Up"],
                  ["aar_approved", "A&R Approved"],
                  ["passed", "Polite Pass"],
                ]}
              />
            )}
            {activeTab === "careers" && (
              <ApplicationsTab
                endpoint="careers"
                idField="c_id"
                nameField="name"
                fields={[["Position", "position"], ["City/State", "city"], ["Phone", "phone"], ["Portfolio", "links"], ["Resume Link", "resume_link"]]}
                statuses={["new", "reviewing", "interview", "hired", "closed"]}
                statusLabels={{ new: "New", reviewing: "Reviewing", interview: "Interview", hired: "Hired", closed: "Closed" }}
                emptyLabel="No career applications yet."
                testPrefix="careers"
                description="People applying to work with the label. Download resumes, schedule interviews, track the pipeline, and mark hires."
                positionsManager
                interviewable
              />
            )}
            {activeTab === "bookings" && <BookingsTab />}
            {activeTab === "tracks" && <TracksTab />}
            {activeTab === "press" && <PressTab />}
            {activeTab === "pitches" && <PitchesTab />}
            {activeTab === "merch" && <MerchTab />}
            {activeTab === "orders" && <OrdersTab />}
            {activeTab === "reviews" && <ReviewsTab />}
            {activeTab === "writer" && <WriterTab />}
            {activeTab === "subscribers" && <SubscribersTab />}
            {activeTab === "team" && <TeamTab />}
            {activeTab === "hr" && <HRTab />}
            {activeTab === "medusa" && <MedusaTab />}
            {activeTab === "arcade" && <ArcadeTab />}
            {activeTab === "drops" && <DropsTab />}
            {activeTab === "demos" && <DemosTab />}
            {activeTab === "activity" && <ActivityTab />}
            {activeTab === "content" && <ContentTab />}
            {activeTab === "knowledge" && <KbTab />}
            {activeTab === "guide" && (
              <div data-testid="guide-tab">
                <p className="text-sm text-ice/55 mb-6 leading-relaxed max-w-2xl">
                  The Label Ops handbook: signing in, fixing login problems, what every tab does, and how to run the HR Suite. Staff only — this guide is not public.
                </p>
                <LabelOpsGuideContent />
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
